/**
 * Template contracts — slice 4, "email templates".
 *
 * This file is the block document: the thing a person is actually making. Three
 * decisions from SLICES shape every schema below, and each one is a whole class
 * of bug designed out rather than defended against; slice 56 added a fourth,
 * about where the frame's settings live.
 *
 * **1. Blocks are the source of truth. MJML and HTML are derived and never
 * edited.** `templates.blocks` is the document; `mjml_source` and
 * `html_compiled` are build output stored beside it. Nothing in this file
 * accepts markup, and every write shape is `.strict()` — so a request that tried
 * to carry hand-written HTML would fail contract parsing rather than depend on
 * a reviewer noticing a stray property in a mapper. The moment anyone can edit
 * the HTML, the blocks stop describing the email.
 *
 * **2. Block text is PLAIN TEXT, escaped at render — never user HTML.** This is
 * why there is no sanitiser anywhere in this slice, and why there must never be
 * one. A sanitiser is a defence with a bypass; a field that cannot hold markup
 * has nothing to bypass. `<script>alert(1)</script>` typed into a heading is a
 * heading that reads `<script>alert(1)</script>`, because the renderer escapes
 * it into a text node. When bold arrived (slice 56) it arrived exactly as this
 * paragraph promised: as structured marks inside the block — a paragraph's
 * {@link textRunSchema} runs, each plain text plus four closed marks — not as a
 * string of markup. It stays true of every field added below, links included
 * if they ever come.
 *
 * **3. Single column, flat, ordered — and the unsubscribe footer is the last
 * block, always.** See {@link TemplateDocument}: the document type is
 * `[...BodyBlock[], FooterBlock]`, a variadic tuple, so "no footer", "two
 * footers" and "a footer in the middle" are not values TypeScript will construct
 * — they are not states to validate, they are states that do not exist. The zod
 * side cannot express a trailing fixed element, so it enforces the same rule as
 * a refinement; the type is the design, the refinement is the runtime guard on
 * data arriving from the wire.
 *
 * **4. Two template-level settings, and they are columns, not blocks** (slice
 * 56): {@link TemplateBodyWidth} and {@link TemplateBackground}. They describe
 * the frame the blocks are rendered in, not any one block, and the document
 * stays an array — `templates_blocks_is_array` and the footer CHECK read it as
 * one — so they live beside `blocks` on the row rather than inside it.
 *
 * Variables (`{{ name }}`, `{{ email }}`, `{{ extras.company }}`) are declared
 * here and substituted in slice 5. This file fixes the syntax, derives the set a
 * contact can actually supply from the contact contract, and refuses an unknown
 * one at save time **by name** — see {@link unresolvableTemplateVariables},
 * which the create and update schemas run themselves.
 *
 * WhatsApp arrived in slice 7 and is additive, exactly as this header predicted:
 * `type` was already the discriminator, so the write shapes below became unions
 * over it and nothing about the email arm changed. The WhatsApp document itself
 * lives in `whatsapp.js` — positional variables, a media header and no
 * attachments are a different document, not a variant of this one.
 *
 * HOSTED IMAGES CHANGED NONE OF THESE DECISIONS, AND THEY ARE A DEPLOYMENT
 * CHOICE RATHER THAN A FACT ABOUT EVERY EMAIL. How an inline image travels is
 * {@link TemplateImageDelivery}, decided once per deployment by whether
 * `TEMPLATE_ASSET_PUBLIC_BASE_URL` is set: unset, images are embedded as `cid:`
 * parts exactly as they always were; set, every one of them is served from a
 * public URL Wend mints for an object Wend stores. Neither mode touches decision
 * 1 — blocks are still the source of truth, and whatever an `<img src>` becomes
 * is substituted at **send** time, never at compile time, so `html_compiled`
 * remains a pure function of the document and the byte-identical preview
 * guarantee survives. Neither touches decision 2: there is still no `src` field
 * and there must never be one. What hosting does change is the argument printed
 * above {@link imageBlockSchema}, half of which it answers and half of which it
 * does not, and the campaign-norm constants below — starting with
 * {@link GMAIL_CLIP_BYTES}, which is the number that argues for switching it on.
 *
 * THE IMPORT CYCLE, AND WHY IT IS SAFE. `whatsapp.ts` imports the target grammar
 * from this file (`parseTemplateVariableName`), and this file imports the
 * WhatsApp document from it. That is a genuine ES module cycle, so **every
 * reference to a `whatsapp.js` value here goes through `z.lazy()`**: whichever
 * of the two modules is evaluated first, nothing in this file reads a binding
 * from the other while it is still uninitialised. Types are imported with
 * `import type`, which is erased and creates no edge at all.
 */
import { z } from 'zod';

import { isoDateTimeSchema, paginated, paginationQuerySchema, sortDirectionSchema, uuidSchema } from '../common.js';
import {
  EMAIL_TEMPLATE_ASSET_KIND_VALUES,
  templateAssetKindSchema,
  templateStatusSchema,
  templateTypeSchema,
} from '../enums.js';
import { EXTRAS_KEY_MAX_LENGTH, contactSchema } from './contact.js';
import { CONTROL_CHARACTERS, publicHttpsBaseUrlSchema } from './public-base-url.js';
import { waTemplateDocumentSchema, waTemplateViewSchema } from './whatsapp.js';

/* -------------------------------------------------------------------------- */
/* Limits                                                                      */
/* -------------------------------------------------------------------------- */

export const MAX_TEMPLATE_NAME_LENGTH = 120;

/**
 * Subject line. 200 rather than a header's theoretical maximum: every mail
 * client truncates far below this, and a subject nobody can read to the end of
 * is a subject that was written for the wrong medium.
 */
export const MAX_SUBJECT_LENGTH = 200;

/** Preheader — the grey line beside the subject in an inbox list. */
export const MAX_PREHEADER_LENGTH = 200;

export const MAX_HEADING_TEXT_LENGTH = 200;
export const MAX_TEXT_BLOCK_LENGTH = 5_000;
export const MAX_BUTTON_TEXT_LENGTH = 80;
export const MAX_IMAGE_ALT_LENGTH = 200;

/**
 * The widest an image block may be *rendered* — the single-column body width
 * every email template has settled on.
 *
 * Named rather than left as a literal inside {@link imageBlockSchema} because
 * {@link EMAIL_IMAGE_MAX_WIDTH_PX} is derived from it, and a derivation from a
 * magic number is not a derivation. It is a **render attribute**: it decides the
 * `width` on the `<img>`, and nothing anywhere resizes the file behind it.
 */
export const MAX_IMAGE_BLOCK_WIDTH_PX = 600;

export const MAX_FOOTER_NOTE_LENGTH = 500;
export const MAX_SENDER_ADDRESS_LENGTH = 300;
export const MAX_UNSUBSCRIBE_LABEL_LENGTH = 60;
export const MAX_HREF_LENGTH = 2_000;

/**
 * Blocks in one document. A guard against a malformed request, not a product
 * limit — a real marketing email is a dozen blocks, and the compile cost is
 * linear.
 */
export const MAX_TEMPLATE_BLOCKS = 100;

/* -------------------------------------------------------------------------- */
/* Campaign norms — what a marketing email is actually judged against           */
/* -------------------------------------------------------------------------- */

/**
 * Where Gmail stops rendering a message body and prints "[Message clipped] View
 * entire message".
 *
 * **The most consequential number in this file, and the reason is which END of
 * the message disappears.** Clipping truncates the *end* of the body. The end of
 * the body is the footer. The footer is where the unsubscribe link is — see
 * {@link footerBlockSchema}, which argues at length that the fastest route to a
 * spam complaint is a recipient who wanted out and could not find how. A clipped
 * unsubscribe manufactures exactly that recipient, and complaint rate is an
 * account-level threshold measured against the **tenant's own** AWS account
 * under bring-your-own-SES. Slice 18 exists because of that threshold; this
 * constant is the same threat arriving through layout instead of through list
 * quality.
 *
 * **OBSERVED AND STABLE, NOT A PUBLISHED GUARANTEE.** Google documents that
 * clipping happens and does not publish the figure; ~102 KB is what senders have
 * measured consistently for years. That is a weaker kind of claim than SES's
 * 10 MB, which is in AWS's own quota table, and the difference is why
 * {@link MESSAGE_BODY_HEADROOM_BYTES} exists rather than budgeting right up to
 * the line. A number treated as a spec when it is really folklore is worse than
 * no number; this one is folklore with a good track record, and is labelled so.
 */
export const GMAIL_CLIP_BYTES = 102 * 1024;

/**
 * What a message costs before a single block of content is in it.
 *
 * Headers, the `text/plain` alternative, the MIME boundaries, and the compiled
 * HTML itself. The live template's `html_compiled` is **9,776 bytes**, so 12 KiB
 * is that plus a little — a measurement with a margin, not a guess dressed up as
 * one.
 *
 * It is always *subtracted from* {@link GMAIL_CLIP_BYTES}, never added to a
 * budget, so everything derived from it errs in the safe direction:
 * under-estimating what fits wastes a few kilobytes, over-estimating it clips a
 * footer.
 */
export const MESSAGE_BODY_HEADROOM_BYTES = 12 * 1024;

/**
 * What base64 costs, as an integer percent.
 *
 *     4 bytes out per 3 in          = 133.33%
 *     plus a CRLF every 76 chars    × 78/76
 *     4/3 × 78/76                   = 136.84%
 *
 * Rounded **up** to 137 and kept an integer so every figure below is exact,
 * reproducible and has no floating point in it. Rounding up is the deliberate
 * half: a budget that under-states the cost of encoding is a budget that clips.
 */
export const BASE64_EXPANSION_PERCENT = 137;

/**
 * The soft per-message budget the send path **logs** against, attachments
 * excluded.
 *
 * **Not a refusal.** `MAX_RAW_MESSAGE_BYTES` (10 MiB, `raw-message.ts`) is the
 * hard refusal, it is SES's own published limit, and it does not move. This is
 * the line above which a marketing email has stopped being one *in Gmail*, and
 * the send path records it so that "our footers are being cut off" is something
 * an operator can see in a log rather than something a recipient discovers and
 * reports as spam.
 *
 * Equal to {@link GMAIL_CLIP_BYTES} **by construction rather than by
 * coincidence** — it is the same fact, named for the place it is used, so the
 * two can never be edited apart. Attachments are excluded because they are not
 * part of the rendered body. **Embedded pictures are excluded too**, where a
 * deployment embeds them: what senders consistently measure the clip against is
 * the HTML, and nobody here has observed a `multipart/related` picture part
 * counting towards it — so a warning that counted one would fire on every message
 * of every image campaign on the evidence of nobody's measurement. See
 * `messageBodyBudget` in the API.
 */
export const TARGET_MESSAGE_BYTES = GMAIL_CLIP_BYTES;

/**
 * How many image bytes would have fitted inside the clip budget — the
 * derivation that argues for hosting. Re-derive it, do not trust it:
 *
 *     (GMAIL_CLIP_BYTES - MESSAGE_BODY_HEADROOM_BYTES) × 100 ÷ BASE64_EXPANSION_PERCENT
 *   = (104,448          - 12,288)                      × 100 ÷ 137
 *   =  92,160                                          × 100 ÷ 137
 *   ≈  67,270 bytes — about 65 KiB.
 *
 * 65 KiB is not a hero banner. It is certainly not the **531,115-byte** JPEG the
 * live template carries, which is eight times the entire budget — which is why
 * every message that campaign sent was 876,369 bytes, and why a deployment with
 * an image host serves every picture from it instead. See
 * {@link imageBlockSchema} and {@link TemplateImageDelivery}.
 *
 * **What this arithmetic assumes, stated.** It is the budget *if* an image's
 * bytes count towards the clip, which they certainly do for a picture inlined
 * into the HTML itself and which nobody here has observed for a separate
 * `multipart/related` part. It is kept because it is the ceiling on the
 * pessimistic reading, and because the case for hosting does not rest on it: an
 * 876 KB message is base64-encoded afresh for, and downloaded in full by, every
 * recipient regardless of what Gmail displays. The send path's soft budget does
 * **not** make this assumption — see {@link TARGET_MESSAGE_BYTES}.
 *
 * **THIS IS NOT A THRESHOLD AND MUST NOT BECOME ONE.** "Inline it under 64 KiB,
 * host it above" was put to the product owner with this arithmetic and rejected:
 * two image paths *in one deployment*, chosen per picture, mean one of them is
 * under-tested, and the under-tested one is always the path carrying the unusual
 * message. Any code that *branches* on this constant has reintroduced the
 * rejected design.
 *
 * The configuration gate is not that design, and the difference is worth being
 * exact about. {@link TemplateImageDelivery} picks **one** path for a whole
 * deployment, from one variable, and every image that deployment sends takes it
 * at any size. The embedded path it keeps is not a rare branch either: it is the
 * path that carried the first 1,116 real messages, kept byte for byte so that
 * `master` can go on sending while no image host exists. It is exported so a
 * reviewer — or a verify script — can reproduce the arithmetic above without
 * taking anyone's word for it.
 */
export const CLIP_BUDGET_INLINE_IMAGE_BYTES = Math.floor(
  ((GMAIL_CLIP_BYTES - MESSAGE_BODY_HEADROOM_BYTES) * 100) / BASE64_EXPANSION_PERCENT,
);

/**
 * The width an email image is worth *storing* at, in pixels.
 *
 * Twice {@link MAX_IMAGE_BLOCK_WIDTH_PX}, and **derived from it rather than
 * typed**, because the doubling is the entire argument: 600px is the
 * single-column body width email settled on, and 2× covers a high-density
 * display without paying for a third scale nothing renders.
 *
 * **Advisory, and it can only ever be advisory.** There is no image resizing
 * anywhere in Wend and there will not be one: `sharp` and every credible
 * alternative is a native binary, and the machine this builds on runs an
 * Application Control policy that blocks one. So this number cannot be enforced
 * by transforming a file — it can only be said out loud in the editor, beside
 * the upload, before somebody drops a 4,000px phone photograph into a
 * newsletter. A comment claiming it is enforced would be inviting the next
 * reader to go looking for a resizer that does not exist.
 */
export const EMAIL_IMAGE_MAX_WIDTH_PX = 2 * MAX_IMAGE_BLOCK_WIDTH_PX;

/**
 * One inline image. Refused **above** this at upload, and nothing is stored when
 * it is refused.
 *
 * **1 MB, down from 2 MB, and the old figure's argument is no longer the one
 * that sizes it.** It read: *"small enough that a template cannot quietly become
 * a 30 MB email: SES rejects a message over 10 MB and the failure would land at
 * send time."* That is still true where images are embedded and has stopped
 * being the binding reason anywhere, for two separate reasons worth stating
 * exactly:
 *
 *   1. **Where an image host is configured, an inline image is not in the
 *      message at all** — see {@link TemplateImageDelivery}. A cap that protects
 *      the MIME body cannot bind bytes that never enter it. Where no host is
 *      configured the image IS embedded, and there 1 MB is simply a tighter
 *      version of the old protection: about 1.37 MB of base64 per picture, far
 *      inside SES's 10 MiB.
 *   2. **What binds a marketing email is Gmail's clip point, not SES's ceiling,
 *      and it binds three orders of magnitude lower.** See
 *      {@link CLIP_BUDGET_INLINE_IMAGE_BYTES}: about 65 KiB of image would have
 *      fitted, against a 10 MiB ceiling. Anything anybody would call a banner
 *      loses to the first limit long before it approaches the second — in either
 *      mode, which is why the cap is not sized against the clip point either.
 *
 * So what does 1 MB actually bound? **The download a recipient pays for, in
 * both modes.** An embedded picture is downloaded with the message and a hosted
 * one is fetched when it is opened, and on this product's list either happens
 * over what is routinely a phone on mobile data in a district town. At
 * {@link EMAIL_IMAGE_MAX_WIDTH_PX} a well-exported JPEG is comfortably under
 * 1 MB; above 1 MB the file is almost always an unexported camera original, and
 * the honest answer to one is to refuse it with a sentence rather than to send it
 * to a thousand people. It applies whether or not a host is configured, because
 * the product owner asked for the cap and not for hosting to come with it.
 *
 * **1 MB is a product judgement, not a published limit** — nothing external
 * blesses it — and it is a *refusal* rather than a resize because there is no
 * resizing (see {@link EMAIL_IMAGE_MAX_WIDTH_PX}). Two consequences of lowering
 * a cap, both deliberate: the live template's 531,115-byte image is still well
 * inside it, so no existing work is invalidated; and nothing re-checks a stored
 * file against this number, so a `template_assets` row already above it keeps
 * working rather than becoming unsendable.
 */
export const MAX_TEMPLATE_ASSET_BYTES = 1 * 1024 * 1024;

/* -------------------------------------------------------------------------- */
/* Attachment limits — still bytes on the wire, because an attachment still is  */
/* -------------------------------------------------------------------------- */

/**
 * One attachment. Larger than an inline image because the files people actually
 * attach — a rate card, a brochure, a price list — are.
 *
 * **This cap is about the bytes a person may upload, and it does not move with
 * the delivery mode.** It used to say attachments were out of the hosting
 * decision entirely — "nothing here is hosted, nothing here gets a `public_id`,
 * and the database refuses one" — and that was true until slice 23, which is the
 * different product decision the old paragraph named. `0024` widened the CHECK it
 * cited; {@link TemplateAttachmentDelivery} carries what changed and what it
 * costs a reader. The cap itself is unchanged in both modes: 10 MB is what a
 * person may put on a template, whether it then travels inside every message or
 * is published once and linked.
 *
 * What the editor says about a file has not stopped being said either — see
 * `attachment_hurts_deliverability` in {@link templateApprovalIssues}, which is
 * about a filter's treatment of an attached file and so is exactly the fault
 * `linked` removes.
 */
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

/**
 * Every attachment on one template, added up.
 *
 * The binding constraint is not disk, it is **the size of the message that goes
 * out**. Attachments are base64-encoded into the MIME body, which inflates them
 * by roughly a third, and the resulting message has to be accepted twice: by
 * SES, and then by the recipient's provider. Gmail refuses anything over 25 MB
 * and it is far from the strictest.
 *
 * 15 MB of files becomes about 20 MB on the wire, which clears that with room
 * for the body. Slice 5 must still check the real figure against SES's own
 * quota at send time rather than trusting this number: this is an authoring
 * guard, and the send path is where a message actually gets refused.
 */
export const MAX_TEMPLATE_ATTACHMENTS_TOTAL_BYTES = 15 * 1024 * 1024;

/**
 * How many files one template may carry.
 *
 * A limit on a number nobody should be near. An email with eleven attachments
 * has a different problem than a byte budget.
 */
export const MAX_TEMPLATE_ATTACHMENTS = 10;

/* -------------------------------------------------------------------------- */
/* Plain text — the field type that has no markup in it                        */
/* -------------------------------------------------------------------------- */

/**
 * A surrogate code unit with no partner — half of a character.
 *
 * Refused for a reason specific to this slice rather than on general principle.
 * `blocks` is `jsonb`, which stores a lone surrogate intact; `html_compiled` is
 * `text`, where Postgres substitutes U+FFFD. A document containing one therefore
 * compiles to HTML that can never equal a recompile of itself, so the editor's
 * "the stored HTML no longer matches this document" alarm fires permanently and
 * no edit clears it. The two columns cannot agree, so the value is refused at
 * the border instead of creating a template that is stuck.
 *
 * These arrive by paste — a spreadsheet cell holding a truncated emoji is the
 * usual way — so the message talks about characters, not code units.
 */
const LONE_SURROGATE = /[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/;

const UNPRINTABLE_MESSAGE = 'This field contains an unprintable or incomplete character';

/* `CONTROL_CHARACTERS` lives in `./public-base-url.js` now, with its reasons, so
   the URL parser there and the field rules here refuse the same characters. */

/**
 * A single-line plain-text field: heading, button label, unsubscribe label.
 *
 * PLAIN TEXT. It may contain `<`, `>`, `&` and quotes, and it means them
 * literally — the renderer escapes them into text. That is the whole reason
 * this slice has no sanitiser: there is no markup here to sanitise.
 */
function plainTextLine(max: number, emptyMessage?: string) {
  return z
    .string()
    .trim()
    // `emptyMessage` names the CONSEQUENCE of leaving this particular field
    // blank, for the fields where the consequence is not obvious from the label.
    // Passing `undefined` keeps zod's own wording, which is right for a heading:
    // nobody needs a paragraph explaining that an empty heading is empty. It is
    // wrong for alt text, where the field looks decorative and is not.
    .min(1, emptyMessage)
    .max(max)
    .refine((value) => !/[\r\n]/.test(value), 'This field is a single line')
    .refine((value) => !CONTROL_CHARACTERS.test(value) && !LONE_SURROGATE.test(value), UNPRINTABLE_MESSAGE);
}

/**
 * A multi-line plain-text field: a paragraph, a postal address, a footer note.
 * Newlines are kept and become line breaks in the compiled output. Still plain
 * text — see {@link plainTextLine}.
 */
function plainTextBody(max: number) {
  return z
    .string()
    .trim()
    .min(1)
    .max(max)
    .refine((value) => !CONTROL_CHARACTERS.test(value) && !LONE_SURROGATE.test(value), UNPRINTABLE_MESSAGE);
}

/* -------------------------------------------------------------------------- */
/* Links — a schema-level refusal, not a UI check                              */
/* -------------------------------------------------------------------------- */

/**
 * The only schemes a template may link to.
 *
 * `javascript:`, `data:`, `file:`, `vbscript:` and everything else are refused
 * **here, at the shape**, not by the editor and not by the renderer. A UI check
 * is a check one `curl` goes around; a contract refusal is one the API cannot
 * accidentally skip, because parsing the request is not optional.
 *
 * `mailto:` is included because a "reply to us" button is an ordinary thing to
 * want and mail clients handle it natively. `tel:` is not: this is an email
 * template, and a phone link belongs in the WhatsApp composer.
 */
export const TEMPLATE_LINK_SCHEMES = ['http', 'https', 'mailto'] as const;
export type TemplateLinkScheme = (typeof TEMPLATE_LINK_SCHEMES)[number];

/** `scheme:` at the very start of the string. RFC 3986 scheme grammar. */
const SCHEME_PATTERN = /^([A-Za-z][A-Za-z0-9+.-]*):/;

/**
 * A link a button may point at.
 *
 * Deliberately hand-parsed rather than fed to `new URL`: `@wend/shared` compiles
 * with `"types": []` and `lib: ["ES2022"]`, so there is no `URL` global to lean
 * on, and the explicit form documents what is refused anyway.
 *
 * What it refuses, and why each one matters:
 *   - **no scheme** (`example.com`, `/pricing`) — a relative link in an email
 *     resolves against nothing and lands the reader on a broken page;
 *   - **protocol-relative** (`//evil.test`) — inherits a scheme that does not
 *     exist in a mail client;
 *   - **any scheme outside the allow-list** — `javascript:` is the obvious one,
 *     `data:text/html` is the one people forget;
 *   - **variables** — `{{ }}` inside a link is refused on purpose. A variable
 *     substituted into a URL is an injection vector unless it is
 *     percent-encoded, and a link that has to be re-parsed after substitution is
 *     a link whose scheme was never really checked. Personalised links are a
 *     v2 conversation with encoding rules of its own.
 */
export const templateHrefSchema = z
  .string()
  .trim()
  .min(1)
  .max(MAX_HREF_LENGTH)
  .superRefine((value, ctx) => {
    if (CONTROL_CHARACTERS.test(value) || /[\s]/.test(value)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'A link cannot contain spaces or control characters' });
      return;
    }
    if (value.includes('{{') || value.includes('}}')) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'A link cannot contain a {{ variable }} — personalised links are not supported',
      });
      return;
    }

    const match = SCHEME_PATTERN.exec(value);
    if (!match?.[1]) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'A link must be absolute and start with https://, http:// or mailto:',
      });
      return;
    }

    const scheme = match[1].toLowerCase();
    if (!(TEMPLATE_LINK_SCHEMES as readonly string[]).includes(scheme)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Links may only use ${TEMPLATE_LINK_SCHEMES.join(', ')} — "${scheme}:" is not allowed`,
      });
      return;
    }

    const rest = value.slice(match[0].length);
    if (scheme === 'mailto') {
      if (!rest.includes('@') || rest.startsWith('@')) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'A mailto: link needs an email address' });
      }
      return;
    }
    // http/https: require an authority, so `https:evil` and `https://` are both out.
    if (!/^\/\/[^/?#]+/.test(rest)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'A web link needs a host, e.g. https://acmeretail.in/offers' });
    }
  });

/* -------------------------------------------------------------------------- */
/* Block primitives                                                            */
/* -------------------------------------------------------------------------- */

/**
 * A block's stable identity.
 *
 * Stable is the operative word: it survives every move, insert and undo, which
 * is what lets the editor key React nodes, target "move this one up", and report
 * a validation problem as "the heading block `h_2f9`" instead of "block 3".
 * Generated by whoever creates the block (the editor or the server), never
 * derived from position — a positional id is an id that changes when you reorder.
 *
 * A small alphabet so an id is safe in a DOM `id`, a JSON pointer and a log line
 * without escaping. UUIDs and nanoids both fit.
 */
export const blockIdSchema = z.string().regex(/^[A-Za-z0-9_-]{1,64}$/, 'Block ids are 1–64 characters of A–Z, a–z, 0–9, _ or -');

export const BLOCK_ALIGN_VALUES = ['left', 'center', 'right'] as const;
export const blockAlignSchema = z.enum(BLOCK_ALIGN_VALUES);
export type BlockAlign = z.infer<typeof blockAlignSchema>;

/** `h1`–`h3`. Deeper levels are a document structure this format does not have. */
export const HEADING_LEVEL_VALUES = [1, 2, 3] as const;
export const headingLevelSchema = z.union([z.literal(1), z.literal(2), z.literal(3)]);
export type HeadingLevel = z.infer<typeof headingLevelSchema>;

/* -------------------------------------------------------------------------- */
/* Blocks                                                                      */
/* -------------------------------------------------------------------------- */

export const headingBlockSchema = z
  .object({
    type: z.literal('heading'),
    id: blockIdSchema,
    /** Plain text. May contain `{{ variables }}`. */
    text: plainTextLine(MAX_HEADING_TEXT_LENGTH),
    level: headingLevelSchema.default(2),
    align: blockAlignSchema.default('left'),
  })
  .strict();
export type HeadingBlock = z.infer<typeof headingBlockSchema>;

/* -------------------------------------------------------------------------- */
/* Inline formatting — structured runs, never stored HTML (slice 56)            */
/* -------------------------------------------------------------------------- */

/**
 * The font sizes a run may carry, in px. A closed list rather than a number: a
 * size is a choice from a menu, and a free number is how a paragraph ends up
 * at 17px beside one at 16px and nobody can say why. 16 is the body default —
 * `email-theme`'s `mj-text` size — and {@link normaliseTextRuns} stores it as
 * an absent `size`, so "the default" has one spelling.
 */
export const TEXT_RUN_SIZE_VALUES = [14, 16, 18, 20, 24, 28, 32] as const;
export type TextRunSize = (typeof TEXT_RUN_SIZE_VALUES)[number];
export const textRunSizeSchema = z.union([
  z.literal(14),
  z.literal(16),
  z.literal(18),
  z.literal(20),
  z.literal(24),
  z.literal(28),
  z.literal(32),
]);

/** What an absent `size` means: the body text size every `mj-text` carries. */
export const DEFAULT_TEXT_RUN_SIZE: TextRunSize = 16;

/**
 * The line height a sized run is rendered with: the size plus 8px.
 *
 * Not a ratio, and the choice is inherited rather than made here: it is the
 * rule every existing style in the email already follows — body 16/24, and the
 * three heading levels 28/36, 22/30 and 18/26 in `email-theme.ts`. A run at
 * 28px therefore sets exactly like an `h1`. Exported so the editor can draw a
 * sized run at the height the email will.
 */
export function textRunLineHeightPx(size: TextRunSize): number {
  return size + 8;
}

/**
 * Runs in one paragraph. A guard against a malformed request, not a product
 * limit: a paragraph where every other word changes style is still well under
 * this.
 */
export const MAX_TEXT_BLOCK_RUNS = 500;

/**
 * One stretch of a paragraph and the marks on it.
 *
 * THIS IS DECISION 2 IN THE HEADER, KEPT. The header said that if bold arrived
 * it would arrive "as structured marks inside the block, not as a string of
 * markup", and this is that shape. `text` is plain text exactly like the
 * paragraph's own — the renderer escapes it — and the marks are four closed
 * fields the renderer turns into four known tags. There is still nothing here a
 * sanitiser could be asked to clean, because there is still no field that can
 * hold markup: `<b>` typed into a run is a run that reads `<b>`.
 *
 * REJECTED:
 *   - **An HTML string from a contenteditable.** The obvious shape and the one
 *     this file was written to refuse; it is the raw-HTML block the owner
 *     stopped, reached by a side door.
 *   - **`{ start, end, mark }` ranges over `text`**, which the header once
 *     sketched. Ranges can overlap, can point past the end, and go stale the
 *     moment `text` is edited without them; every consumer would have to
 *     re-derive runs from them anyway. Runs cannot overlap by construction.
 *
 * Each flag is `true` or absent — never `false` — so a run has one spelling and
 * two runs with the same marks compare equal field by field. `.strict()`: a
 * colour, a font or a link is not a mark this format has, and is refused rather
 * than dropped.
 */
export const textRunSchema = z
  .object({
    text: z.string().max(MAX_TEXT_BLOCK_LENGTH),
    bold: z.literal(true).optional(),
    italic: z.literal(true).optional(),
    underline: z.literal(true).optional(),
    size: textRunSizeSchema.optional(),
  })
  .strict();
export type TextRun = z.infer<typeof textRunSchema>;

/** A run as the editor may build one: flags may be `false`, size may be 16. */
export interface TextRunInput {
  readonly text: string;
  readonly bold?: boolean;
  readonly italic?: boolean;
  readonly underline?: boolean;
  readonly size?: TextRunSize;
}

/** The canonical marks of a run: `false` and the default size dropped. */
function canonicalRun(run: TextRunInput): TextRun {
  return {
    text: run.text,
    ...(run.bold === true ? { bold: true as const } : {}),
    ...(run.italic === true ? { italic: true as const } : {}),
    ...(run.underline === true ? { underline: true as const } : {}),
    ...(run.size === undefined || run.size === DEFAULT_TEXT_RUN_SIZE ? {} : { size: run.size }),
  };
}

function sameMarks(a: TextRun, b: TextRun): boolean {
  return a.bold === b.bold && a.italic === b.italic && a.underline === b.underline && a.size === b.size;
}

/**
 * THE one normaliser. The editor calls it before it sends; the contract calls it
 * on every parse. One rule, not two — a second implementation in the editor
 * would be the first thing to disagree with this one.
 *
 * In order:
 *   1. **Canonical marks** — `false` flags and `size: 16` are dropped.
 *   2. **The paragraph's own trim.** `text` is parsed with `.trim()`, so runs
 *      whose join began or ended in whitespace would break the
 *      `text === join(runs)` invariant for a reason nobody typed. The same
 *      `String.prototype.trim` is applied to the concatenation, and the cut is
 *      taken out of whichever runs it falls in.
 *   3. **Empty runs dropped.**
 *   4. **Adjacent runs with identical marks merged**, so a boundary exists only
 *      where the formatting actually changes.
 *
 * Idempotent: normalising a normalised list returns an equal list.
 */
export function normaliseTextRuns(runs: readonly TextRunInput[]): TextRun[] {
  const canonical = runs.map(canonicalRun);

  const joined = canonical.map((run) => run.text).join('');
  let dropStart = joined.length - joined.trimStart().length;
  let dropEnd = joined.length - joined.trimEnd().length;

  const trimmed: TextRun[] = canonical.map((run) => ({ ...run }));
  for (const run of trimmed) {
    if (dropStart === 0) break;
    const cut = Math.min(dropStart, run.text.length);
    run.text = run.text.slice(cut);
    dropStart -= cut;
  }
  for (let index = trimmed.length - 1; index >= 0 && dropEnd > 0; index -= 1) {
    const run = trimmed[index] as TextRun;
    const cut = Math.min(dropEnd, run.text.length);
    run.text = run.text.slice(0, run.text.length - cut);
    dropEnd -= cut;
  }

  const merged: TextRun[] = [];
  for (const run of trimmed) {
    if (run.text === '') continue;
    const previous = merged[merged.length - 1];
    if (previous !== undefined && sameMarks(previous, run)) {
      previous.text += run.text;
    } else {
      merged.push({ ...run });
    }
  }
  return merged;
}

/**
 * Whether normalised runs say anything the paragraph's `text` does not — any
 * mark on any run. When they do not, the document parse drops `runs` from the
 * block, so an unformatted paragraph is stored, compiled and sent exactly as it
 * was before runs existed.
 */
export function textRunsCarryFormatting(runs: readonly TextRun[]): boolean {
  return runs.some(
    (run) => run.bold === true || run.italic === true || run.underline === true || run.size !== undefined,
  );
}

/** One reason a text block's runs cannot be stored. */
export interface TextRunIssue {
  readonly message: string;
}

/** A `{{ variable }}` token that a run boundary falls strictly inside of. */
export interface SplitTemplateVariable {
  /** Index of the token's first `{`, in the runs joined. */
  readonly start: number;
  /** Index one past its last `}`. */
  readonly end: number;
  /** The token as written, braces and spaces included. */
  readonly token: string;
}

/** Where one run ends and the next begins, in the runs joined. The last run's end is not a boundary. */
function runBoundaries(runs: readonly { readonly text: string }[]): number[] {
  const boundaries: number[] = [];
  let offset = 0;
  for (const run of runs.slice(0, -1)) {
    offset += run.text.length;
    boundaries.push(offset);
  }
  return boundaries;
}

/**
 * Every `{{ variable }}` token that a run boundary cuts — rule 2 of
 * {@link textBlockRunIssues}, and the only place that rule is written. A
 * boundary AT a token's edge (`start` or `end`) does not cut it: that is the
 * whole variable formatted, or none of it.
 *
 * Exported so the editor asks this exact question rather than restating it —
 * the check it runs while typing and the one the save runs cannot drift apart.
 * It reads the runs alone: the tokens are found in their joined text, which
 * {@link textBlockRunIssues} has already required to equal the block's `text`.
 */
export function variablesSplitByRuns(runs: readonly { readonly text: string }[]): SplitTemplateVariable[] {
  const boundaries = runBoundaries(runs);
  const text = runs.map((run) => run.text).join('');
  const split: SplitTemplateVariable[] = [];

  const pattern = templateVariablePattern();
  let match = pattern.exec(text);
  while (match !== null) {
    const start = match.index;
    const end = start + match[0].length;
    if (boundaries.some((boundary) => boundary > start && boundary < end)) {
      split.push({ start, end, token: match[0] });
    }
    match = pattern.exec(text);
  }
  return split;
}

/**
 * Why a text block's runs are refused — empty when they are fine. Run by the
 * document schema on every text block, and exported so the editor can ask the
 * same question before it saves.
 *
 * **1. `text === runs.map(r => r.text).join('')`.** This invariant is what lets
 * every existing reader of `.text` — the plain-text part, variable detection,
 * the save-time variable check, previews, search — ignore runs entirely and
 * still be right. A block where the two disagree has two contents, and which
 * one a recipient sees would depend on which part of the message they read.
 *
 * **2. No run boundary inside a `{{ variable }}`** — {@link variablesSplitByRuns},
 * which the editor calls too. Substitution runs on the
 * *compiled HTML*, where a boundary becomes a tag: `{{ na</strong>me }}` is not
 * a token any more, so the reader would receive the braces. Refused rather than
 * repaired, because "repaired" means silently changing which words are bold.
 *
 * **3. No boundary inside a character or a line break.** Each run is escaped
 * and wrapped on its own, so a boundary between the two halves of a surrogate
 * pair (an emoji) would emit two lone surrogates — which `html_compiled`, a
 * `text` column, stores as U+FFFD; and one between `\r` and `\n` would render
 * one line break as two.
 */
export function textBlockRunIssues(block: { readonly text: string; readonly runs?: readonly TextRun[] }): TextRunIssue[] {
  if (block.runs === undefined) return [];
  const issues: TextRunIssue[] = [];

  const joined = block.runs.map((run) => run.text).join('');
  if (joined !== block.text) {
    issues.push({ message: 'The formatted text does not match the paragraph text.' });
    return issues;
  }

  for (const { token } of variablesSplitByRuns(block.runs)) {
    issues.push({
      message: `Formatting cannot start or stop inside the variable "${token}". Format the whole variable or none of it.`,
    });
  }

  for (const boundary of runBoundaries(block.runs)) {
    const before = block.text.charCodeAt(boundary - 1);
    const after = block.text.charCodeAt(boundary);
    const splitsPair = before >= 0xd800 && before <= 0xdbff && after >= 0xdc00 && after <= 0xdfff;
    const splitsBreak = before === 0x0d && after === 0x0a;
    if (splitsPair || splitsBreak) {
      issues.push({ message: 'Formatting cannot start or stop inside a character or a line break.' });
      break;
    }
  }

  return issues;
}

export const textBlockSchema = z
  .object({
    type: z.literal('text'),
    id: blockIdSchema,
    /**
     * Plain text, newlines preserved as line breaks. May contain variables.
     *
     * **Always the whole paragraph, formatted or not.** When `runs` is present
     * this is their concatenation — see {@link textBlockRunIssues} — so nothing
     * that reads `text` has to know formatting exists.
     */
    text: plainTextBody(MAX_TEXT_BLOCK_LENGTH),
    /**
     * Slice 56: bold, italic, underline and size within the paragraph.
     *
     * Optional, and **absent means exactly the block that existed before** — the
     * same stored shape, the same compiled bytes. Normalised on every parse by
     * {@link normaliseTextRuns}; the invariants that need `text` beside it are
     * checked by {@link templateDocumentSchema}, because a zod object inside a
     * discriminated union cannot carry a refinement of its own; and runs that
     * carry no mark are dropped there too (see {@link textRunsCarryFormatting}).
     */
    runs: z.array(textRunSchema).max(MAX_TEXT_BLOCK_RUNS).transform(normaliseTextRuns).optional(),
    align: blockAlignSchema.default('left'),
  })
  .strict();
export type TextBlock = z.infer<typeof textBlockSchema>;

/* -------------------------------------------------------------------------- */
/* How an inline image travels — decided per deployment, never per picture      */
/* -------------------------------------------------------------------------- */

/**
 * How the inline images in an email reach the reader, **for a whole
 * deployment**.
 *
 *   - `embedded` — each picture travels inside the message as a `cid:` part in
 *     `multipart/related`. This is what Wend always did, and it is what carried
 *     the first real campaign: 1,118 recipients, 1,116 accepted by SES, on 16
 *     September 2026, every message 876,369 bytes because a 531,115-byte JPEG
 *     rode inside each one.
 *   - `hosted` — **every** picture, at **any** size, is served from Wend's image
 *     host and the message carries only its URL. No image bytes are in the
 *     message at all, and a picture with no public address refuses the campaign
 *     at schedule time (`template_images_not_hostable`) instead of arriving
 *     broken.
 *
 * ## Why it is a gate at all
 *
 * As first built, hosting was unconditional, and that made every template with a
 * picture *impossible to schedule* until an image host existed. That is right
 * once a host exists and wrong for the deployment sending real campaigns today,
 * which has no bucket and no host. SLICES states the rule this follows: **a
 * change that would block a send ships behind configuration.** So `embedded` is
 * not a fallback and not a degraded mode — it is the behaviour `master` already
 * sends with, kept byte for byte until somebody switches the host on.
 *
 * ## One source of truth, and where it lives
 *
 * The value is derived **once**, by the API, from whether
 * {@link TEMPLATE_ASSET_PUBLIC_BASE_URL_ENV} is set (`config.service.ts`), and
 * handed to everything that needs it: the upload path that mints public ids, the
 * send path that rewrites `<img src>`, the schedule-time gathering that feeds
 * {@link import('./campaign.js').campaignScheduleIssues}, and the editor through
 * {@link templateSchema}. **This package never reads the environment**; a rule
 * here is told the mode, exactly as it is told everything else.
 *
 * Two alternatives were rejected by name. **A second variable** ("HOST_IMAGES=true"
 * beside the URL) creates the two states that must not exist — hosting switched
 * on with nowhere to host, and a URL configured that nothing uses — and the first
 * of those is the broken-picture failure the whole design is built to prevent.
 * **A per-template flag** is the size threshold the product owner rejected under
 * a different name: two image paths inside one deployment, one of them always the
 * rare and under-tested one.
 *
 * An enum rather than a boolean because the two values name what happens to the
 * bytes, and because `!hostsImages` is one character from the inversion this
 * repository has shipped several times. Compare with `===`.
 */
export const TEMPLATE_IMAGE_DELIVERY_VALUES = ['embedded', 'hosted'] as const;
export const templateImageDeliverySchema = z.enum(TEMPLATE_IMAGE_DELIVERY_VALUES);
export type TemplateImageDelivery = z.infer<typeof templateImageDeliverySchema>;

/* -------------------------------------------------------------------------- */
/* How an attachment travels — the same gate, and a different consequence       */
/* -------------------------------------------------------------------------- */

/**
 * How a template's attachments reach the reader, **for a whole deployment**.
 *
 *   - `attached` — every file travels inside the message, base64-encoded, one
 *     copy per recipient. What Wend has always done: the first real campaign
 *     carried a 98,530-byte PDF as about 135 KB of base64 in every one of its
 *     copy: 1,118 recipients, 1,116 accepted by SES.
 *   - `linked` — the file is served from Wend's asset host and the message
 *     carries a link to it. No attachment bytes are in the message at all.
 *
 * ## The same gate as {@link TemplateImageDelivery}, deliberately
 *
 * This is **derived from** the image mode — {@link attachmentDeliveryFor} — and
 * is not its own variable. One host is configured or none is, and a deployment
 * that hosts pictures has somewhere to put a PDF. A second variable would create
 * the four states of two booleans, of which two are incoherent (hosting files
 * with nowhere to host them, a host configured that only half the assets use),
 * and the design that rejected a per-picture size threshold rejects a per-kind
 * switch for the same reason: the rarely-taken path is the under-tested one.
 *
 * It is a **separate vocabulary** all the same, because the two modes differ in
 * what they change and the words have to be honest about it. Hosting a picture
 * is invisible to a reader whose client loads remote content: the same picture
 * appears in the same place. Hosting an attachment is not invisible to anybody.
 * The paperclip goes away. A reader who forwards the mail forwards a link rather
 * than a file, a reader who saves it offline has nothing saved, and a link can
 * be clicked long after the campaign, by whoever has the mail. That is the
 * trade against the certain cost of the other mode — the file re-encoded and
 * re-sent for every recipient, and every marketing filter treating an
 * attachment harshly whatever its size. The editor says which of the two will
 * happen, in the recipient's terms, before anything is scheduled.
 *
 * ## What is not decided here
 *
 * **Click tracking is not switched on by this**, in either mode. A linked file
 * is a link in the body like any other: if the workspace has click tracking on,
 * Amazon rewrites it with every other link, and if it does not, nothing rewrites
 * it. Nothing in the attachment path names a configuration set, adds a
 * redirect, or mints a per-recipient address — the URL is one public id per
 * file, the same in every copy of the message.
 */
export const TEMPLATE_ATTACHMENT_DELIVERY_VALUES = ['attached', 'linked'] as const;
export const templateAttachmentDeliverySchema = z.enum(TEMPLATE_ATTACHMENT_DELIVERY_VALUES);
export type TemplateAttachmentDelivery = z.infer<typeof templateAttachmentDeliverySchema>;

/**
 * The attachment mode that goes with an image mode. The only place the two are
 * joined, so a deployment cannot end up hosting one and embedding the other.
 *
 * A `Record` rather than a ternary: adding a third image mode then fails to
 * compile here, which is the point at which somebody has to decide what it means
 * for a file, instead of inheriting whichever branch the `?:` happened to have.
 */
const ATTACHMENT_DELIVERY_BY_IMAGE_DELIVERY: Readonly<
  Record<TemplateImageDelivery, TemplateAttachmentDelivery>
> = {
  embedded: 'attached',
  hosted: 'linked',
};

export function attachmentDeliveryFor(
  imageDelivery: TemplateImageDelivery,
): TemplateAttachmentDelivery {
  return ATTACHMENT_DELIVERY_BY_IMAGE_DELIVERY[imageDelivery];
}

/**
 * What the editor says about a template's files, **before anything is
 * scheduled** — slice 23's third done-when.
 *
 * Written from the reader's side of the screen rather than the deployment's: a
 * person uploading a brochure wants to know what lands in their customer's
 * inbox, not which environment variable is set. Neither sentence apologises for
 * its mode; both are states somebody chose.
 *
 * Owned here rather than typed into `AttachmentsPanel` for the reason every
 * sentence about a mode is: the campaign screens say the same thing in their own
 * words at schedule time, and two copies of one fact drift.
 */
export const TEMPLATE_ATTACHMENT_DELIVERY_SUMMARY: Readonly<
  Record<TemplateAttachmentDelivery, string>
> = {
  attached: 'Files are sent with the message',
  linked: 'Files are sent as download links',
};

export const TEMPLATE_ATTACHMENT_DELIVERY_DETAIL: Readonly<
  Record<TemplateAttachmentDelivery, string>
> = {
  attached:
    'Each file travels inside every copy of the email, so it arrives as an attachment the reader can open offline. It is also copied once per recipient, and marketing mail carrying an attachment is filtered harshly whatever the file is.',
  linked:
    'Each file is published to Wend’s asset host and the email carries a link to it under the footer. The message stays small and carries no attachment, and anyone holding the email — or the link — can download the file. Removing it from the template stops it going out; once a campaign has sent a link to it, the published copy is kept so that link keeps working.',
};

/**
 * The heading above the links, in the message itself.
 *
 * "Attachments" and not "Downloads" or "Files": it is the word a reader already
 * has for "the thing that came with this email", and the point of the section is
 * that the paperclip they would have looked for is not there.
 */
export const ATTACHMENT_SECTION_HEADING = 'Attachments';

/* -------------------------------------------------------------------------- */
/* Alt text — the block itself, for a large minority of readers                */
/* -------------------------------------------------------------------------- */

/**
 * The one line that stays on the alt-text field.
 *
 * It says what to type and who reads it, and it makes **no claim about how many
 * clients block images** — "most mail clients block images by default" is
 * email-marketing folklore of exactly the kind this repository has shipped
 * before, and the argument for alt text does not need it.
 */
export const TEMPLATE_IMAGE_ALT_DESCRIPTION =
  'Describe what the picture says. A reader whose client blocks images sees this text in its place.';

/**
 * The paragraph behind the disclosure — progressive, not deleted — **in the
 * version that is true of this deployment**.
 *
 * Keyed on {@link TemplateImageDelivery} because the two modes make different
 * claims and only one of them is ever true. With a host, this is the half that
 * got *more* true when pictures stopped travelling inside the message: a `cid:`
 * image renders in a client that blocks **remote** content, and a hosted one does
 * not, so for that reader the alt text is no longer a fallback with a picture
 * behind it — it is the block. Without a host the picture *is* inside the
 * message, and saying it is "fetched from Wend" would be describing a deployment
 * the reader is not using; what stays true there is the smaller claim, that a
 * reader with pictures off or a screen reader gets only the text.
 *
 * A `Record` rather than two constants so a third mode would be a compile error
 * here rather than a paragraph that silently describes the wrong one. Somebody
 * choosing words deserves to know which is true, and it is one click away rather
 * than in their face on every image.
 */
export const TEMPLATE_IMAGE_ALT_DETAIL: Readonly<Record<TemplateImageDelivery, string>> = {
  hosted:
    'Pictures in an email are fetched from Wend when the message is opened, rather than travelling inside it. A client set to block remote content therefore shows no picture at all — only this text — so write what the picture would have told them (“30% off winter stock until 31 January”) rather than what it is (“banner”).',
  embedded:
    'Pictures in an email travel inside the message. A reader who has turned pictures off, or who listens with a screen reader, still gets only this text — so write what the picture would have told them (“30% off winter stock until 31 January”) rather than what it is (“banner”).',
};

/**
 * Why an empty one is refused, in the words the refusal uses.
 *
 * `imageBlockSchema.alt` is a {@link plainTextLine} whose minimum is 1, and this
 * is the message that minimum carries — so the contract's refusal, the API's
 * field error and the editor's own check are one sentence rather than three.
 * Without it a person saw zod's "String must contain at least 1 character(s)",
 * which describes a type and not a consequence.
 *
 * It is declared **above** `imageBlockSchema` rather than beside the rest of the
 * image copy further down, and that is not tidiness: these are module-level
 * constants evaluated in source order, and a schema built from a `const`
 * declared later in the file reads `undefined` at import time. The failure is
 * silent — zod simply falls back to its own wording — which is the worst
 * available way for a sentence to go missing.
 */
export const TEMPLATE_IMAGE_ALT_REQUIRED_MESSAGE =
  'Alt text is required: without it, a reader whose client blocks images sees an empty gap where this block should be.';

/**
 * An inline image.
 *
 * **`assetId` references a `template_assets` row. There is deliberately no
 * `src`, and there must never be one.** That is unchanged, and the argument for
 * it is unchanged. What the compiled email *does* with the asset now depends on
 * the deployment — {@link TemplateImageDelivery} — and where it is hosted the two
 * halves of the original objection live in different places and have to be read
 * apart rather than as one paragraph.
 *
 * ## Unchanged: no author-typed `src`
 *
 * An arbitrary URL typed into a template is an **open redirect and a phishing
 * surface**. Whatever we compile into an `<img src>` is fetched by the
 * recipient's client under the tenant's own sending reputation, and the target
 * can be swapped for something else the day after the campaign is approved —
 * *because we do not own it*. That last clause carries the whole load of the
 * argument, which is precisely why the argument does **not** transfer to what
 * changed below: a URL Wend mints, for an object Wend stored, in a bucket Wend
 * controls, cannot be swapped by anyone the template author can reach.
 *
 * ## Changed where an image host is configured: every image is HOSTED, at any size
 *
 * The bytes have always travelled inside the message as a `cid:` part, and
 * **they still do in a deployment with no image host** — that path sent the
 * first 1,116 real messages and it is kept exactly as it was, because switching
 * it off before a host existed would make every template with a picture
 * unschedulable. Where `TEMPLATE_ASSET_PUBLIC_BASE_URL` is set they do not
 * travel at all. The send path substitutes an absolute URL built from
 * `template_assets.public_id` — a value **the server generates**, under **Wend's
 * own bucket and Wend's own credentials**, never the tenant's AWS account.
 * (Sending as the tenant's identity requires their account; hosting a picture
 * does not, and reaching for their sealed SES credentials to serve a JPEG would
 * be borrowing an authority the task does not need.) See
 * {@link templateAssetPublicUrl}.
 *
 * The forcing constraint was arithmetic, not taste: every message of the first
 * campaign was 876,369 bytes, base64-encoded afresh for and downloaded in full by
 * every recipient, because of one 531,115-byte picture. And on the pessimistic
 * reading of Gmail's clip — see {@link CLIP_BUDGET_INLINE_IMAGE_BYTES}, which
 * says what that reading assumes — roughly 65 KiB of image would fit before a
 * clipped message loses its footer, which is where the unsubscribe link is.
 *
 * There is **no size threshold and no per-template toggle**: in a hosting
 * deployment every image is hosted, at every size, and in an embedding one every
 * image is embedded. The rejected alternative was "inline under 64 KiB, host
 * above it", rejected because two image paths in one deployment mean one of them
 * is under-tested and it is always the rare path that ends up carrying the
 * unusual message. Re-introducing a threshold re-introduces that.
 *
 * ## NOT changed by hosting: the tracking leak. It is still real.
 *
 * The original objection had two halves and hosting answers only one. The other
 * was: *"a remote image is a request carrying the reader's IP and client to a
 * third party who was never named to anyone."* **Owning the bucket does not
 * remove the request.** It changes who receives it — Wend rather than a stranger
 * — which is a smaller problem, not the absence of one. A reader with images
 * enabled tells Wend's image host that this message was opened, from roughly
 * there, on that client, whether or not open tracking was ever switched on for
 * the campaign.
 *
 * Nothing in this contract can fix that, and a comment implying the objection
 * has gone away would be false. Two things are true and one is promised.
 *
 * **True, by construction:** the URL carries **no recipient-specific component**
 * — one public id per asset, shared by every copy of every message that uses
 * the picture — so the URL alone does not say who fetched it.
 *
 * **True, and the reason the first is not anonymity:** an earlier version of this
 * paragraph said a fetch "cannot be attributed to a person even by whoever holds
 * the logs", and that was wrong. The request carries the reader's IP address and
 * client. `campaign_recipients.sent_at` records when each recipient's copy left.
 * A warm-up wave releases a small batch spread over time, and a test send goes to
 * one person — so a fetch seconds after one recipient's send is attributable to
 * that recipient by timing, and the same public id stays stable across every
 * campaign that reuses the template.
 *
 * **Promised, and only promised:** the image host keeps **no access log**. For
 * the S3 bucket that means server access logging is off; for a CloudFront
 * distribution in front of it, standard logging and real-time logs are off.
 * With no log there is nothing to join against the send times. That is an
 * operational commitment about how a bucket is run, written where the operator
 * configures the host (`apps/api/.env.example`, beside
 * `TEMPLATE_ASSET_PUBLIC_BASE_URL`, and the deployment notes in
 * `s3.storage.ts`). This file does not enforce it and nothing in the application
 * can. If it stops being true, this paragraph is what has stopped being true:
 * rewrite it, do not delete it.
 *
 * The public URL is also deliberately **not** the storage key. The key names the
 * tenant, and a tenant id in an `<img src>` is a stable correlation identifier
 * sitting in plain view in the message source: two emails collected from two
 * different people join into "these two bank with the same cooperative bank" —
 * a sensitive inference in a market covered by the DPDP Act. See
 * {@link templateAssetPublicIdSchema}.
 *
 * ## `alt` is required, and hosting is what makes it load-bearing
 *
 * It was always required, in both modes. It matters far more where images are
 * hosted. A `cid:` image is *part of the message* and renders in a client that
 * blocks **remote** content; a hosted one does not. So for every recipient whose
 * client blocks remote images — a large minority, and the default in several
 * clients — **the alt text is the block**, with nothing behind it. That is why
 * the field is
 * `plainTextLine`, whose minimum length is 1, rather than an optional string:
 * an empty alt is a blank rectangle where the offer was, and no write shape in
 * this file will accept a document that leaves it out. The sentence that refusal
 * carries is {@link TEMPLATE_IMAGE_ALT_REQUIRED_MESSAGE}, declared above.
 */
export const imageBlockSchema = z
  .object({
    type: z.literal('image'),
    id: blockIdSchema,
    /** A `template_assets` row belonging to this template. Never a URL. */
    assetId: uuidSchema,
    /**
     * Required in both delivery modes, and the refusal says why — for a reader
     * with pictures off this text is the whole block, and where images are
     * hosted that includes every reader who blocks remote content. See
     * {@link TEMPLATE_IMAGE_ALT_REQUIRED_MESSAGE}.
     */
    alt: plainTextLine(MAX_IMAGE_ALT_LENGTH, TEMPLATE_IMAGE_ALT_REQUIRED_MESSAGE),
    /**
     * Rendered width in pixels, or null for the container width. Capped at
     * {@link MAX_IMAGE_BLOCK_WIDTH_PX}, the single-column body width every email
     * template has settled on.
     *
     * **A render attribute and nothing more.** It sets the `width` on the
     * `<img>`; it does not resize, re-encode or otherwise touch the stored file,
     * because nothing in Wend does — see {@link EMAIL_IMAGE_MAX_WIDTH_PX}. A
     * 4,000px photograph shown at 600px is still a 4,000px photograph on the
     * recipient's connection.
     */
    width: z.number().int().min(16).max(MAX_IMAGE_BLOCK_WIDTH_PX).nullable().default(null),
    align: blockAlignSchema.default('center'),
  })
  .strict();
export type ImageBlock = z.infer<typeof imageBlockSchema>;

/**
 * A call to action.
 *
 * `href` is {@link templateHrefSchema}: http, https or mailto, absolute, and no
 * variables. `javascript:` is refused by the schema, so it is refused for the
 * API, the editor, an import and anything else that ever writes a template —
 * which is the point of putting it here rather than in a form validator.
 */
export const buttonBlockSchema = z
  .object({
    type: z.literal('button'),
    id: blockIdSchema,
    /** The label. Plain text; may contain variables. */
    text: plainTextLine(MAX_BUTTON_TEXT_LENGTH),
    href: templateHrefSchema,
    align: blockAlignSchema.default('center'),
  })
  .strict();
export type ButtonBlock = z.infer<typeof buttonBlockSchema>;

export const dividerBlockSchema = z
  .object({
    type: z.literal('divider'),
    id: blockIdSchema,
  })
  .strict();
export type DividerBlock = z.infer<typeof dividerBlockSchema>;

export const spacerBlockSchema = z
  .object({
    type: z.literal('spacer'),
    id: blockIdSchema,
    /** Vertical space in pixels. */
    height: z.number().int().min(4).max(120).default(24),
  })
  .strict();
export type SpacerBlock = z.infer<typeof spacerBlockSchema>;

/**
 * The footer. **Every template has exactly one, and it is last.**
 *
 * The *block* is not optional and is not something a person adds — it is part
 * of what a template is, expressed in {@link TemplateDocument}. What became
 * optional is the **unsubscribe link inside it**, via `includeUnsubscribe`.
 *
 * ## Why the link is a choice and the block is not
 *
 * The two things this block carries have different authorities behind them, and
 * folding them together was the mistake:
 *
 *   - the **postal address** is required by CAN-SPAM, CASL and EU
 *     sender-identification rules, and those bite on where the *recipient* is.
 *     It stays enforced, at approval, for every email template.
 *   - the **unsubscribe link** is required for *commercial* mail. Not every
 *     email a tenant sends is commercial: a cooperative bank mailing its own
 *     members an AGM notice, a statement or a service announcement is sending
 *     transactional mail, and an unsubscribe link on it is both wrong and
 *     confusing — there is nothing to unsubscribe *from*, and offering the
 *     option implies the recipient can opt out of notices they cannot.
 *
 * So the link defaults to **on** and comes off deliberately. That default is
 * the load-bearing half: a marketing email without a working unsubscribe is how
 * a tenant's own AWS account gets suspended, and under bring-your-own-SES that
 * lands on the tenant rather than on Wend. Slice 18 exists because complaint
 * rate is an account-level threshold, and **the fastest way to a complaint is a
 * recipient who wants out and cannot find how** — they press the spam button
 * instead, which costs strictly more than an unsubscribe does.
 *
 * Unchecking it is therefore a real decision with a real consequence, and the
 * editor says so at the point of the decision rather than here.
 *
 * What it holds, and what it deliberately does not:
 *
 *   - `includeUnsubscribe` — whether the link is rendered at all. Default
 *     `true`, so a template nobody thought about carries one, and so every
 *     document written before this field existed parses with the behaviour it
 *     was compiled under.
 *   - `unsubscribeLabel` — the link text only. **There is no `unsubscribeHref`
 *     field.** The URL is minted per recipient by slice 6's one-click endpoint
 *     and substituted at send; a field here would let a template point its
 *     unsubscribe link at a page that unsubscribes nobody, which is worse than
 *     having no link at all because it looks compliant. The illegal state is
 *     unrepresentable rather than validated.
 *   - `senderAddress` — the sender's business postal address, rendered as the
 *     small print at the foot of the email.
 *
 *     Required by **CAN-SPAM** (US), **CASL** (Canada) and EU sender-identification
 *     rules, and those bite on where the *recipient* is, not where the tenant is —
 *     which is why an India-first product still enforces it. Note what is
 *     deliberately **not** claimed here: Gmail's and Yahoo's 2024 bulk-sender
 *     requirements cover SPF/DKIM/DMARC, one-click unsubscribe and a spam rate
 *     under 0.3%, and say nothing about a postal address. An earlier version of
 *     this comment and its UI copy asserted that inbox providers score you on it.
 *     They do not, as far as any published guidance says, and a compliance
 *     reason that turns out to be folklore is worse than no reason: it teaches
 *     people to discount the real ones next to it.
 *
 *     **Nullable here on purpose**: a template is created before
 *     anyone has typed an address, and a required field would be filled with
 *     "TBC" and shipped. It is instead a blocker for approval — see
 *     {@link templateApprovalIssues} — so the template cannot be *used* without
 *     it, which is where the requirement actually bites.
 *   - `note` — optional context, e.g. "You are receiving this because you bought
 *     from us in the last year". Plain text, like everything else.
 */
export const footerBlockSchema = z
  .object({
    type: z.literal('footer'),
    id: blockIdSchema,
    /**
     * Whether the unsubscribe link is rendered.
     *
     * **Defaults to `true`, and the default is the point.** Every template
     * document written before this field existed parses unchanged and keeps the
     * link it was compiled with, so this is additive rather than a migration.
     */
    includeUnsubscribe: z.boolean().default(true),
    /** Link text only. The URL is minted per recipient at send time. */
    unsubscribeLabel: plainTextLine(MAX_UNSUBSCRIBE_LABEL_LENGTH).default('Unsubscribe'),
    /** Postal address of the sender. Null until typed; blocks approval. */
    senderAddress: plainTextBody(MAX_SENDER_ADDRESS_LENGTH).nullable().default(null),
    /** Optional "why am I getting this" line. */
    note: plainTextBody(MAX_FOOTER_NOTE_LENGTH).nullable().default(null),
  })
  .strict();
export type FooterBlock = z.infer<typeof footerBlockSchema>;

/** The default unsubscribe label, so the editor and the seed cannot disagree. */
export const DEFAULT_UNSUBSCRIBE_LABEL = 'Unsubscribe';

/* -------------------------------------------------------------------------- */
/* The document                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Everything that is not the footer. Used as the element type of the body, which
 * is how "a footer cannot appear in the middle" stops being a rule and starts
 * being a type error.
 */
export const bodyBlockSchema = z.discriminatedUnion('type', [
  headingBlockSchema,
  textBlockSchema,
  imageBlockSchema,
  buttonBlockSchema,
  dividerBlockSchema,
  spacerBlockSchema,
]);
export type BodyBlock = z.infer<typeof bodyBlockSchema>;

/** Any block, footer included. The element type of a document read off the wire. */
export const templateBlockSchema = z.discriminatedUnion('type', [
  headingBlockSchema,
  textBlockSchema,
  imageBlockSchema,
  buttonBlockSchema,
  dividerBlockSchema,
  spacerBlockSchema,
  footerBlockSchema,
]);
export type TemplateBlock = z.infer<typeof templateBlockSchema>;

export const TEMPLATE_BLOCK_TYPE_VALUES = [
  'heading',
  'text',
  'image',
  'button',
  'divider',
  'spacer',
  'footer',
] as const;
export type TemplateBlockType = (typeof TEMPLATE_BLOCK_TYPE_VALUES)[number];

/**
 * The document: **zero or more body blocks, then exactly one footer.**
 *
 * This is a variadic tuple, and it is the answer to "make the illegal state
 * unrepresentable rather than validating it later". TypeScript checks all three
 * halves of the rule for free:
 *
 * ```ts
 * const ok:   TemplateDocument = [heading, button, footer];  // fine
 * const none: TemplateDocument = [heading];                  // error: missing footer
 * const two:  TemplateDocument = [footer, footer];           // error: FooterBlock is not a BodyBlock
 * const mid:  TemplateDocument = [footer, heading, footer];  // error, same reason
 * ```
 *
 * It stays an **array** on the wire and in `templates.blocks`, exactly as SLICES
 * requires — a tuple *is* an array, so nothing about the storage, the ordering
 * or a later "add columns" change is affected.
 *
 * What the type cannot do is check data arriving from an HTTP body, so
 * {@link templateDocumentSchema} re-states the same rule as a refinement. That
 * is the one place the rule is written twice, and it is written twice on purpose:
 * the type is the design, the refinement is the border guard.
 */
export type TemplateDocument = [...BodyBlock[], FooterBlock];

/**
 * The runtime half of {@link TemplateDocument}.
 *
 * zod has no combinator for "a variadic prefix followed by a fixed last element"
 * — `z.tuple().rest()` puts the rest *after* the fixed items, which is the wrong
 * way round — so the footer rule is a refinement. The final cast is what lets
 * the inferred type stay the exact tuple; it is safe precisely because the
 * refinement above it has just proven the shape.
 */
export const templateDocumentSchema = z
  .array(templateBlockSchema)
  .max(MAX_TEMPLATE_BLOCKS)
  .superRefine((blocks, ctx) => {
    if (blocks.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'A template must contain at least the unsubscribe footer',
      });
      return;
    }

    const footerIndexes = blocks.flatMap((block, index) => (block.type === 'footer' ? [index] : []));
    const lastIndex = blocks.length - 1;

    if (footerIndexes.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [lastIndex],
        message:
          'The unsubscribe footer is missing. Every template ends with a footer block; it cannot be removed.',
      });
    } else if (footerIndexes.length > 1) {
      for (const index of footerIndexes.slice(1)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [index],
          message: 'A template has exactly one unsubscribe footer',
        });
      }
    } else if (footerIndexes[0] !== lastIndex) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [footerIndexes[0] as number],
        message: 'The unsubscribe footer must be the last block',
      });
    }

    // Duplicate ids break every editor operation that targets one block —
    // move-up, delete, and the "which block is wrong" half of a save error.
    const seen = new Set<string>();
    blocks.forEach((block, index) => {
      if (seen.has(block.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [index, 'id'],
          message: `Duplicate block id "${block.id}" — block ids are unique within a template`,
        });
      }
      seen.add(block.id);

      // Slice 56. The run invariants need `text` beside `runs`, and
      // `textBlockSchema` cannot hold a refinement and stay a member of the
      // discriminated union — so they are checked here, on the block's path.
      if (block.type === 'text') {
        for (const issue of textBlockRunIssues(block)) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: [index, 'runs'], message: issue.message });
        }
      }
    });
  })
  // Safe by construction: the refinement above has just proven this array is a
  // run of body blocks ending in exactly one footer, which is what the tuple
  // type says. TypeScript cannot follow that proof, so it is asserted here once
  // rather than at every call site.
  //
  // The map is slice 56's: runs that carry no mark are removed from the block,
  // key and all, so an unformatted paragraph is stored as the block that
  // existed before runs did and takes the renderer's original path.
  .transform((blocks) => blocks.map(withoutUnformattedRuns) as TemplateDocument);

/** A text block whose runs say nothing beyond `text` loses them. */
function withoutUnformattedRuns(block: TemplateBlock): TemplateBlock {
  if (block.type !== 'text' || !('runs' in block)) return block;
  if (block.runs !== undefined && textRunsCarryFormatting(block.runs)) return block;
  const { runs: _unformatted, ...rest } = block;
  return rest;
}

/** The footer of a document — always present, because the type says so. */
export function documentFooter(document: TemplateDocument): FooterBlock {
  return document[document.length - 1] as FooterBlock;
}

/** The body blocks of a document, footer excluded. */
export function documentBody(document: TemplateDocument): BodyBlock[] {
  return document.slice(0, -1) as BodyBlock[];
}

/**
 * A new, valid document: one heading, one paragraph, and the footer.
 *
 * Lives here rather than in the API so that "the unsubscribe footer is present
 * in every new template" is a property of the contract package both the API and
 * (later) any import path share, instead of a line of service code somebody can
 * forget. `makeId` is injected because id generation is a runtime concern and
 * `@wend/shared` has no runtime dependency beyond zod.
 */
export function createDefaultDocument(makeId: () => string): TemplateDocument {
  return [
    { type: 'heading', id: makeId(), text: 'Hello {{ name }}', level: 2, align: 'left' },
    {
      type: 'text',
      id: makeId(),
      text: 'Write your message here.',
      align: 'left',
    },
    {
      type: 'footer',
      id: makeId(),
      // Spelled out rather than left to the schema default, so the seed states
      // the product's position instead of inheriting it: a new template carries
      // an unsubscribe link, and taking it off is a decision somebody makes.
      includeUnsubscribe: true,
      unsubscribeLabel: DEFAULT_UNSUBSCRIBE_LABEL,
      senderAddress: null,
      note: null,
    },
  ];
}

/* -------------------------------------------------------------------------- */
/* Variables — syntax, the resolvable set, and the validator                   */
/* -------------------------------------------------------------------------- */

/**
 * The variable syntax: `{{ name }}`, `{{ email }}`, `{{ extras.<key> }}`.
 *
 * The capture is `[^{}]*` rather than a strict identifier grammar on purpose.
 * `extras` keys are **spreadsheet column headers** — "Company Name", "Order
 * Total", "Signup date" — so a token grammar that only allowed `\w+` would make
 * most of a real upload unreferenceable. Everything between the braces is
 * captured, trimmed and then *validated* by {@link parseTemplateVariableName},
 * which is what turns a typo into a named error instead of a silent non-match.
 *
 * A `{{` with no closing `}}` is not a variable at all: it is literal text, it
 * is escaped at render like any other text, and it reaches the inbox as `{{`.
 */
export const TEMPLATE_VARIABLE_PATTERN_SOURCE = '\\{\\{([^{}]*)\\}\\}';

/**
 * A **fresh** global matcher every call.
 *
 * A module-level `/g` regex carries `lastIndex` between calls, so two documents
 * scanned in a row would give different answers depending on where the previous
 * scan stopped. That is the kind of bug that shows up as "the second template
 * saved in a request loses a variable", so the regex is never shared.
 */
export function templateVariablePattern(): RegExp {
  return new RegExp(TEMPLATE_VARIABLE_PATTERN_SOURCE, 'g');
}

/** `extras.` — the prefix that reaches an uploaded column. */
export const EXTRAS_VARIABLE_PREFIX = 'extras.';

/**
 * The contact fields a variable may name, **derived from the contact contract**.
 *
 * `contactSchema.keyof().extract([...])` is a real derivation, not a copy: if
 * `email` were renamed or removed in `./contact.js` this line stops compiling,
 * and the names are the contract's own keys rather than strings re-typed here.
 *
 * It is a deliberate *selection* rather than "every field on a contact", and it
 * fails closed: `id`, `datasetId` and `createdAt` are Wend's bookkeeping, not
 * anything a person would put in an email, and a contact field added by a later
 * slice does **not** silently become addressable in a customer's template until
 * somebody adds it here on purpose.
 *
 * `extras` is excluded because it is not addressable as a whole — a variable
 * reaches one key of it, through {@link EXTRAS_VARIABLE_PREFIX}.
 */
export const contactVariableNameSchema = contactSchema.keyof().extract(['name', 'email', 'phoneE164']);
export type ContactVariableName = z.infer<typeof contactVariableNameSchema>;

/** The same set as a list, for error messages and the editor's insert menu. */
export const CONTACT_VARIABLE_NAMES: readonly ContactVariableName[] = contactVariableNameSchema.options;

/** `{{ name }}` — the canonical rendering of a variable, for messages and the UI. */
export function templateVariableToken(name: string): string {
  return `{{ ${name} }}`;
}

export const TEMPLATE_VARIABLE_ISSUE_REASON_VALUES = [
  /** `{{ }}` or `{{   }}` — braces with nothing in them. */
  'empty',
  /** A name that is not a contact field and does not start `extras.`. */
  'unknown_field',
  /** `extras.` with nothing after it, or a key longer than a column name can be. */
  'invalid_extras_key',
] as const;
export const templateVariableIssueReasonSchema = z.enum(TEMPLATE_VARIABLE_ISSUE_REASON_VALUES);
export type TemplateVariableIssueReason = z.infer<typeof templateVariableIssueReasonSchema>;

/** What a well-formed variable resolves to. */
export type ParsedTemplateVariable =
  | { readonly ok: true; readonly kind: 'contact'; readonly name: ContactVariableName }
  | { readonly ok: true; readonly kind: 'extras'; readonly key: string }
  | { readonly ok: false; readonly reason: TemplateVariableIssueReason };

/**
 * Validates one variable name — the whole of the "which variables can a contact
 * supply" question, in one place.
 *
 * An `extras.<key>` variable is always **resolvable**, and that is not a
 * loophole. Extras keys differ per dataset and per contact, so no save-time
 * check could know whether `extras.company` exists for the audience a campaign
 * has not chosen yet; refusing one would refuse the feature. A missing extras
 * key renders as empty at send, which is slice 5's substitution rule. What this
 * *does* catch is the mistake people actually make — writing `{{ company }}`
 * and expecting an uploaded column — and it says so by name.
 */
export function parseTemplateVariableName(raw: string): ParsedTemplateVariable {
  const name = raw.trim();
  if (name === '') return { ok: false, reason: 'empty' };

  if (name.startsWith(EXTRAS_VARIABLE_PREFIX)) {
    const key = name.slice(EXTRAS_VARIABLE_PREFIX.length).trim();
    if (key === '' || key.length > EXTRAS_KEY_MAX_LENGTH) {
      return { ok: false, reason: 'invalid_extras_key' };
    }
    return { ok: true, kind: 'extras', key };
  }

  const contact = contactVariableNameSchema.safeParse(name);
  if (contact.success) return { ok: true, kind: 'contact', name: contact.data };

  return { ok: false, reason: 'unknown_field' };
}

/** Where in a template a variable was written. Used to name the offender. */
export type TemplateVariableLocation =
  | { readonly kind: 'subject' }
  | { readonly kind: 'preheader' }
  | { readonly kind: 'block'; readonly blockId: string; readonly blockType: TemplateBlockType; readonly field: string };

export interface TemplateVariableOccurrence {
  /** As written, trimmed: `name`, `extras.Company Name`. */
  readonly name: string;
  /** As it appeared in the source, braces included. */
  readonly token: string;
  readonly location: TemplateVariableLocation;
}

export interface TemplateVariableIssue extends TemplateVariableOccurrence {
  readonly reason: TemplateVariableIssueReason;
  /** Names the offending variable and where it is. Safe to show a customer. */
  readonly message: string;
}

/** The parts of a template a variable may be written in. */
export interface TemplateVariableSource {
  readonly blocks: readonly TemplateBlock[];
  readonly subject?: string | null;
  readonly preheader?: string | null;
}

/**
 * Which fields of a block carry text — and therefore variables.
 *
 * The single place that knows this. The extractor below and any renderer that
 * substitutes must agree exactly about which fields are personalised; two lists
 * would drift, and the way they drift is that a variable validates at save and
 * then arrives as literal `{{ name }}` in somebody's inbox.
 *
 * `href` is absent deliberately — see {@link templateHrefSchema}.
 */
export function blockTextFields(block: TemplateBlock): { readonly field: string; readonly value: string }[] {
  switch (block.type) {
    case 'heading':
    case 'text':
      return [{ field: 'text', value: block.text }];
    case 'button':
      return [{ field: 'text', value: block.text }];
    case 'image':
      return [{ field: 'alt', value: block.alt }];
    case 'footer':
      return [
        ...(block.note === null ? [] : [{ field: 'note', value: block.note }]),
        ...(block.senderAddress === null ? [] : [{ field: 'senderAddress', value: block.senderAddress }]),
      ];
    case 'divider':
    case 'spacer':
      return [];
  }
}

/** Every `{{ … }}` in one string, in order. */
export function extractVariablesFromText(
  text: string,
  location: TemplateVariableLocation,
): TemplateVariableOccurrence[] {
  const pattern = templateVariablePattern();
  const found: TemplateVariableOccurrence[] = [];
  let match = pattern.exec(text);
  while (match !== null) {
    found.push({ name: (match[1] ?? '').trim(), token: match[0], location });
    match = pattern.exec(text);
  }
  return found;
}

/** Every variable occurrence in a template, subject and preheader included. */
export function collectTemplateVariables(source: TemplateVariableSource): TemplateVariableOccurrence[] {
  const found: TemplateVariableOccurrence[] = [];

  if (source.subject) found.push(...extractVariablesFromText(source.subject, { kind: 'subject' }));
  if (source.preheader) found.push(...extractVariablesFromText(source.preheader, { kind: 'preheader' }));

  for (const block of source.blocks) {
    for (const { field, value } of blockTextFields(block)) {
      found.push(
        ...extractVariablesFromText(value, {
          kind: 'block',
          blockId: block.id,
          blockType: block.type,
          field,
        }),
      );
    }
  }

  return found;
}

/**
 * The distinct variable names a template uses, sorted.
 *
 * This is exactly what is stored in `templates.variables`: it is derived from
 * the document, never typed by anyone, and re-derived on every save. Sorted so
 * the stored value is stable — an unstable order would make `variables` differ
 * between two saves of an unchanged document, which is noise in an audit and a
 * spurious diff in a review.
 */
export function templateVariableNames(source: TemplateVariableSource): string[] {
  const names = new Set(collectTemplateVariables(source).map((occurrence) => occurrence.name));
  return [...names].sort();
}

function describeLocation(location: TemplateVariableLocation): string {
  switch (location.kind) {
    case 'subject':
      return 'the subject line';
    case 'preheader':
      return 'the preheader';
    case 'block':
      return `the ${location.blockType} block "${location.blockId}"`;
  }
}

/**
 * Which variables in a template cannot be resolved — the save-time refusal.
 *
 * "An unknown variable is a validation error at save, not a blank in someone's
 * inbox." Each issue names the offending token and where it is, and suggests the
 * repair that is almost always right: an unrecognised bare name is nearly always
 * an uploaded column that wanted the `extras.` prefix.
 *
 * Called by {@link createTemplateRequestSchema} and
 * {@link updateTemplateRequestSchema} themselves, so the refusal happens during
 * request parsing rather than depending on a service remembering to ask.
 */
export function unresolvableTemplateVariables(source: TemplateVariableSource): TemplateVariableIssue[] {
  const issues: TemplateVariableIssue[] = [];

  for (const occurrence of collectTemplateVariables(source)) {
    const parsed = parseTemplateVariableName(occurrence.name);
    if (parsed.ok) continue;

    const where = describeLocation(occurrence.location);
    const known = CONTACT_VARIABLE_NAMES.map(templateVariableToken).join(', ');
    const message =
      parsed.reason === 'empty'
        ? `Empty variable "${occurrence.token}" in ${where}. Name a field, e.g. ${templateVariableToken('name')}.`
        : parsed.reason === 'invalid_extras_key'
          ? `"${occurrence.token}" in ${where} does not name an uploaded column. Use ${templateVariableToken('extras.Company')} with the column's own name.`
          : `Unknown variable "${occurrence.token}" in ${where}. A contact can supply ${known}, or any uploaded column as ${templateVariableToken(`extras.${occurrence.name}`)}.`;

    issues.push({ ...occurrence, reason: parsed.reason, message });
  }

  return issues;
}

/* -------------------------------------------------------------------------- */
/* Preview sample values                                                       */
/* -------------------------------------------------------------------------- */

/**
 * The values a preview substitutes, **clearly labelled as samples**.
 *
 * Obviously fictional on purpose. A preview showing a real contact's name would
 * be a preview a person can screenshot into a deck, and "Priya Sharma" reads as
 * an example in a way that a plausible-looking real row does not.
 *
 * SECURITY, for whoever writes the substitution step: a sample value — and a
 * real contact value in slice 5 — is substituted into **compiled HTML**, after
 * escaping has already happened for the block text around it. The substituted
 * value must therefore be HTML-escaped by the substituter itself. It is the one
 * place in this slice where a string reaches HTML without having passed through
 * the block renderer, so it is the one place the "plain text, escaped at render"
 * guarantee can be lost.
 */
export const SAMPLE_CONTACT_VARIABLE_VALUES: Readonly<Record<ContactVariableName, string>> = {
  name: 'Priya Sharma',
  email: 'priya.sharma@example.com',
  phoneE164: '+919812345678',
};

/** The notice a preview must render beside sample values. */
export const SAMPLE_VALUES_NOTICE = 'Sample values — not real contact data.';

/** The sample a preview uses for one variable. Extras get a visibly fake value. */
export function sampleValueForVariable(name: string): string {
  const parsed = parseTemplateVariableName(name);
  if (!parsed.ok) return '';
  if (parsed.kind === 'contact') return SAMPLE_CONTACT_VARIABLE_VALUES[parsed.name];
  return `Sample ${parsed.key}`;
}

/** Sample values for every variable a template uses. */
export function sampleValuesForTemplate(source: TemplateVariableSource): Record<string, string> {
  const values: Record<string, string> = {};
  for (const name of templateVariableNames(source)) values[name] = sampleValueForVariable(name);
  return values;
}

/* -------------------------------------------------------------------------- */
/* Approval readiness                                                          */
/* -------------------------------------------------------------------------- */

export const TEMPLATE_APPROVAL_BLOCKER_VALUES = [
  'missing_subject',
  'missing_sender_address',
  'unresolvable_variable',
  'empty_body',
  'attachment_hurts_deliverability',
] as const;
export const templateApprovalBlockerSchema = z.enum(TEMPLATE_APPROVAL_BLOCKER_VALUES);
export type TemplateApprovalBlocker = z.infer<typeof templateApprovalBlockerSchema>;

/**
 * Whether an issue stops approval or merely warns about it.
 *
 * The postal address is the reason this exists. It is legally required for
 * recipients in the US, Canada and the EU, and required by nothing at all for a
 * list that is entirely Indian — which is the launch market. Blocking approval
 * on it made the common case pay for the rare one, so it is `advice`: shown
 * every time, refused never. A tenant sending abroad fills it in; a tenant
 * mailing Mumbai is not stopped by a rule that does not apply to them.
 *
 * `blocker` is reserved for things that make a template unsendable on its own
 * terms — no subject, no message, a variable that resolves to nothing.
 *
 * `attachment_hurts_deliverability` is the second member of the advice half, and
 * it is advice for a sharper version of the same reason: attaching a file is a
 * legitimate thing to do — the live template carries a sponsorship PDF somebody
 * meant to send — and refusing it would be Wend overruling a customer about
 * their own mail. What it is not is free, and the person choosing it should read
 * the cost before scheduling rather than infer it from a bounce report.
 */
export const TEMPLATE_ISSUE_SEVERITY_VALUES = ['blocker', 'advice'] as const;
export const templateIssueSeveritySchema = z.enum(TEMPLATE_ISSUE_SEVERITY_VALUES);
export type TemplateIssueSeverity = z.infer<typeof templateIssueSeveritySchema>;

/**
 * What an attachment costs a send, in one sentence.
 *
 * **Its own function rather than a string built inside
 * {@link templateApprovalIssues}, because two screens have to say it and they
 * are not the same screen.** The approval issue list lives in the editor's
 * *preview* tab; the button that attaches a file lives in its *edit* tab. A
 * person who attaches a price list and never opens the preview would otherwise
 * read this sentence for the first time after the campaign had gone out — which
 * is the one moment it is no use. One function, called from both, so the two
 * cannot end up making slightly different claims about filtering.
 *
 * WHAT IT MAY AND MAY NOT CLAIM, WHICH IS THE CAREFUL PART. That a file travels
 * inside every copy of the message is arithmetic and is stated flatly. That
 * attachments correlate with filtering is **widely reported by senders and
 * published as a rule by no inbox provider** — Gmail's and Yahoo's 2024
 * bulk-sender requirements cover authentication, one-click unsubscribe and spam
 * rate, and say nothing about attachments. So the first half is asserted and the
 * second is labelled as what it is. This repository has shipped a compliance
 * claim that turned out to be folklore, and the cost was not the claim: it was
 * that people then discount the real ones printed beside it.
 *
 * It ends by saying nothing here refuses the file, because it does not. Advice
 * that reads like a refusal teaches people to ignore advice.
 */
export function attachmentDeliverabilityMessage(count: number): string {
  const subject =
    count === 1
      ? 'The file attached to this template travels'
      : `The ${String(count)} files attached to this template travel`;
  return (
    `${subject} inside every single copy of the message — a campaign to 1,000 people sends the ` +
    `${count === 1 ? 'file' : 'files'} 1,000 times — and senders widely report that attachments draw more ` +
    `filtering, though no inbox provider publishes that as a rule. A link to the file in the body usually ` +
    `reaches more inboxes. Nothing here refuses it.`
  );
}

export interface TemplateApprovalIssue {
  readonly blocker: TemplateApprovalBlocker;
  readonly severity: TemplateIssueSeverity;
  readonly message: string;
}

/**
 * The issues that actually refuse approval.
 *
 * Every API refusal path filters through this rather than through the full list,
 * so "shown in the editor" and "refused by the server" cannot drift into meaning
 * the same thing again.
 */
export function templateApprovalBlockers(input: TemplateApprovalInput): TemplateApprovalIssue[] {
  return templateApprovalIssues(input).filter((issue) => issue.severity === 'blocker');
}

/**
 * What {@link templateApprovalIssues} reads.
 *
 * `attachmentCount` is **optional, and `undefined` means "nobody counted"** —
 * not zero. The two are different statements and the difference decides whether
 * a sentence is printed: a caller that did not read the asset rows must not
 * produce "this template has no attachments" out of its own ignorance, so when
 * it is absent the attachment advice is simply not offered. The save and preview
 * paths in `templates.service.ts` do read them and do pass it; anything else
 * that starts calling this and cares about the warning has to read them too.
 */
export interface TemplateApprovalInput {
  readonly blocks: TemplateDocument;
  readonly subject?: string | null;
  readonly preheader?: string | null;
  /** Attachments on the template. `undefined` = not counted, which is not `0`. */
  readonly attachmentCount?: number;
  /**
   * How this deployment carries those files — {@link TemplateAttachmentDelivery}.
   *
   * `undefined` means the caller did not say, and it is treated as `attached`:
   * every deployment carried its files inside the message until slice 23, so a
   * caller that has not been told about the mode is, by construction, one of
   * those. The failure of forgetting is therefore a sentence about a cost that is
   * real — not a screen claiming a cost has gone away when it has not.
   */
  readonly attachmentDelivery?: TemplateAttachmentDelivery;
}

/**
 * What stops a template leaving `draft` — derived once, here, for the same
 * reason `sesReadiness` is: the API decides whether to allow the transition and
 * the editor decides whether to enable the button, and two implementations of
 * "is this template finished" drift until one of them forgets the footer address.
 *
 * The database carries the **subject** half of this as
 * `templates_email_has_subject_before_approval`, because a subject is a column
 * and a CHECK can see it. The rest cannot be a CHECK: the sender address lives
 * inside a jsonb block, and "every variable resolves" needs the contact contract
 * that only this package has. Migration `0007_templates.sql` says the same thing
 * from the other side.
 */
export function templateApprovalIssues(input: TemplateApprovalInput): TemplateApprovalIssue[] {
  const issues: TemplateApprovalIssue[] = [];
  const footer = documentFooter(input.blocks);

  if (!input.subject || input.subject.trim() === '') {
    issues.push({
      blocker: 'missing_subject',
      severity: 'blocker',
      message: 'An email template needs a subject line before it can be approved.',
    });
  }
  if (documentBody(input.blocks).length === 0) {
    issues.push({
      blocker: 'empty_body',
      severity: 'blocker',
      message: 'A template of nothing but the footer has no message in it.',
    });
  }
  if (footer.senderAddress === null) {
    issues.push({
      blocker: 'missing_sender_address',
      // Advice, not a refusal — see TEMPLATE_ISSUE_SEVERITY_VALUES. India, the
      // launch market, requires no postal address in marketing email.
      severity: 'advice',
      message:
        'This template has no business address in its footer. Nothing in India requires one, so it is fine to send without it — but marketing email to recipients in the US, Canada or the EU is legally required to carry one, and that depends on where the recipient is rather than where you are. Add it before sending anywhere those rules reach.',
    });
  }
  /*
   * The attachment warning. `undefined` is "not counted" and says nothing;
   * `0` is "counted, and there are none" and also says nothing. Only a real
   * count above zero produces a sentence — which is why the comparison is
   * explicit rather than `if (input.attachmentCount)`, a test that cannot tell
   * the two silent cases apart and would read as deliberate to nobody.
   *
   * What the sentence may and may not claim is the careful part. That a file
   * travels inside every copy of the message is arithmetic. That attachments
   * correlate with filtering is widely reported by senders and is published as a
   * rule by no inbox provider — Gmail's and Yahoo's 2024 bulk-sender
   * requirements cover authentication, one-click unsubscribe and spam rate, and
   * say nothing about attachments. So the first half is stated and the second is
   * labelled, for the same reason the postal-address copy above had to be
   * rewritten: a compliance reason that turns out to be folklore teaches people
   * to discount the real ones beside it.
   */
  /*
   * SLICE 23. The sentence is about **an attached file**, and where a deployment
   * links to files instead there is no attachment in the message for a filter to
   * treat harshly — so it is not said. This is not the advice being softened: it
   * is the one fault `linked` actually removes, and leaving the sentence
   * standing would be the stale justification this panel has already shipped
   * once, arguing a tenant out of attaching a price list that now costs their
   * deliverability nothing.
   *
   * What `linked` costs instead is the reader's, not the sender's — no offline
   * copy, a link rather than a file when the mail is forwarded — and that is
   * said where the file is chosen, by
   * {@link TEMPLATE_ATTACHMENT_DELIVERY_DETAIL}. It is not an approval issue: it
   * is a property of the deployment, not a fault in this template, and an
   * approval list that fills up with facts nobody can act on is a list people
   * stop reading.
   *
   * `!==`, so an unset mode still raises it — see `attachmentDelivery`.
   */
  if (
    input.attachmentCount !== undefined &&
    input.attachmentCount > 0 &&
    input.attachmentDelivery !== 'linked'
  ) {
    issues.push({
      blocker: 'attachment_hurts_deliverability',
      severity: 'advice',
      message: attachmentDeliverabilityMessage(input.attachmentCount),
    });
  }

  for (const issue of unresolvableTemplateVariables({ blocks: input.blocks, subject: input.subject, preheader: input.preheader })) {
    issues.push({ blocker: 'unresolvable_variable', severity: 'blocker', message: issue.message });
  }

  return issues;
}

/* -------------------------------------------------------------------------- */
/* Assets                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * MIME types an inline image may be.
 *
 * SVG is deliberately absent and must stay absent: an SVG is a script container,
 * and while most mail clients refuse to render one at all, the file would still
 * be served from our origin to anyone with the link. PNG, JPEG, GIF and WebP are
 * what email clients actually render.
 */
export const TEMPLATE_IMAGE_MIME_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'] as const;
export const templateImageMimeTypeSchema = z.enum(TEMPLATE_IMAGE_MIME_TYPES);
export type TemplateImageMimeType = z.infer<typeof templateImageMimeTypeSchema>;

export function isTemplateImageMimeType(value: string): value is TemplateImageMimeType {
  return (TEMPLATE_IMAGE_MIME_TYPES as readonly string[]).includes(value);
}

/* -------------------------------------------------------------------------- */
/* What an inline image upload SAYS — one wording, three places                */
/* -------------------------------------------------------------------------- */

/**
 * The sentences a person reads when a picture is too big, or nearly.
 *
 * **Here rather than in the component or the service, because there are three
 * mouths and one voice.** The browser refuses an oversized file before it is
 * sent (so nobody waits for a megabyte to upload in order to be told no), the
 * API refuses it again in `acceptAssetUpload` (a check the browser can be walked
 * around is not a check), and multer refuses it a third time if it ever gets
 * past both. Three copies of "that image is too large" drift, and the one that
 * drifts is the one quoting a limit that is no longer the limit —
 * `asset-upload-error.interceptor.ts` exists because exactly that happened with
 * the 20 MB spreadsheet ceiling.
 *
 * The rule these obey, from slice 17's text rule: **the honest part stays
 * visible and the reasoning folds.** What a person cannot act without — the
 * file's size, the limit, and what to do — is in the sentence. Why Wend will not
 * do it for them is in the file headers above, not on the screen.
 */

const BYTES_IN_MB = 1024 * 1024;

/** `1,048,577` — grouped by hand, because `@wend/shared` has no `Intl`. */
function groupDigits(value: number): string {
  return String(Math.trunc(value)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * `1 MB`, `1.4 MB`, `612 KB`. One decimal, and only when it is not whole.
 *
 * Deliberately the same rendering `formatFileSize` in `@wend/ui` produces **for
 * every size this file can be handed** — which tops out at
 * {@link MAX_ATTACHMENT_BYTES}, so the two agree everywhere they are both
 * reachable. (It has no gigabyte case; that one would be a size no template file
 * can reach, and an unreachable branch is a branch nobody tests.) The point of
 * the agreement is that the size printed beside an uploaded image and the size
 * quoted in a refusal are one rounding of one number: shown "0.9 MB" in one
 * place and "921 KB" in the other, a person reasonably concludes something
 * moved.
 *
 * It is duplicated rather than imported because `@wend/shared` cannot depend on
 * the UI package, and because the API has to print these sentences too.
 */
export function formatTemplateAssetBytes(bytes: number): string {
  /*
   * Ungrouped, and that is the half of the agreement that was broken.
   *
   * The only four-digit value this branch can ever see is 1,023 — a byte count
   * of 1,024 is already a kilobyte — and `groupDigits` rendered it "1,023 B"
   * where `formatFileSize` renders "1023 B". One value, but the paragraph above
   * promises the two agree *for every size this file can be handed*, and a
   * promise with one counterexample is the kind of claim this repository has
   * learned to stop making rather than to keep quietly. Grouping still applies
   * where it is doing real work — `imageSizeFigures`' exact-byte escape, which
   * prints seven-digit figures nobody can read otherwise.
   */
  if (bytes < 1024) return `${String(Math.trunc(bytes))} B`;
  const value = bytes < BYTES_IN_MB ? bytes / 1024 : bytes / BYTES_IN_MB;
  const unit = bytes < BYTES_IN_MB ? 'KB' : 'MB';
  const rounded = value >= 10 ? String(Math.round(value)) : value.toFixed(1).replace(/\.0$/, '');
  return `${rounded} ${unit}`;
}

/**
 * The file's size and the cap it broke, rendered so **the file can never read as
 * being exactly the size of the limit it exceeded**.
 *
 * Lifted deliberately from `waMediaSizeFigures` in `whatsapp-media.ts`, which
 * found this the hard way: rounding is right for a limit — *1 MB* is how a
 * person holds it in their head — and produces a self-contradicting sentence for
 * anything just over one. Every file from 1,048,577 to 1,101,004 bytes renders
 * as "1 MB", so the refusal read *"that image is 1 MB; the limit is 1 MB"*,
 * which refuses a file for exceeding a figure it was just said to equal and
 * leaves no number in the sentence to act on. The band is reachable: a 1.02 MB
 * JPEG is an ordinary export.
 *
 * So the rounding stays until it collides, and then **both** figures drop to
 * exact bytes together. Dropping only one would trade a contradiction for an
 * apples-and-oranges comparison, which is the same defect better dressed.
 *
 * **A COLLISION IS NOT ALWAYS A LIE, AND THE FIRST VERSION OF THIS TREATED IT AS
 * ONE.** The escape above fires whenever the two renderings match, which is
 * right for the refusal — where `byteSize > maxBytes` always holds, so equal
 * renderings are always a rounding artefact — and wrong for
 * {@link templateImageNearLimitMessage}, which is also allowed to describe a
 * file *at* the cap. A file of exactly {@link MAX_TEMPLATE_ASSET_BYTES} is an
 * ordinary export, it is accepted rather than refused, and it rendered as
 * *"This image is 1,048,576 bytes, close to the 1,048,576 bytes limit"*: an
 * unreadable figure a person cannot hold in their head, in a sentence claiming a
 * value is close to itself. Real equality keeps the rounded figures; it is
 * {@link templateImageNearLimitMessage} that then has to stop saying "close to",
 * because that is where the remaining falsehood lives.
 */
function imageSizeFigures(byteSize: number, maxBytes: number): { size: string; cap: string } {
  const size = formatTemplateAssetBytes(byteSize);
  const cap = formatTemplateAssetBytes(maxBytes);
  if (size !== cap) return { size, cap };
  // `===`, and it is the whole point of this branch: the figures collide either
  // because rounding hid a difference or because there is no difference to hide,
  // and only the first is a sentence that needs rescuing.
  if (byteSize === maxBytes) return { size, cap };
  return { size: `${groupDigits(byteSize)} bytes`, cap: `${groupDigits(maxBytes)} bytes` };
}

/**
 * Where the editor starts saying something about an image's size — the last
 * fifth of {@link MAX_TEMPLATE_ASSET_BYTES}.
 *
 * **A judgement, not a measurement, and nothing external blesses it.** Four
 * fifths is late enough that an ordinary well-exported banner never trips it and
 * early enough to be a warning rather than a commiseration: the band from here
 * to the cap is where the *next* export from the same phone, or the same banner
 * with one more product on it, crosses the line and is refused outright.
 *
 * Derived from the cap rather than typed, so lowering the cap again moves this
 * with it. It warns and refuses nothing — the only refusal is the cap itself.
 */
export const TEMPLATE_IMAGE_NEAR_LIMIT_BYTES = Math.floor((MAX_TEMPLATE_ASSET_BYTES * 4) / 5);

/**
 * The refusal, in full, for a picture above the cap.
 *
 * Every clause earns its place and the order is the order a person needs them:
 * which file, how big it is, what the limit is, **that nothing was stored**, and
 * what to do next. The last is the one an error message usually omits and the
 * only one that gets the person unstuck — so it names a width, from
 * {@link EMAIL_IMAGE_MAX_WIDTH_PX}, rather than saying "make it smaller".
 *
 * It says "Wend does not resize pictures" out loud because the obvious
 * assumption is that it does, and a person who assumes it will keep retrying the
 * same file. That sentence is also true for a reason that is not going to
 * change — see {@link EMAIL_IMAGE_MAX_WIDTH_PX}.
 *
 * **"Usually a fraction of the size", hedged deliberately.** Re-exporting a
 * 4,000px photograph at 1,200px collapses it, which is the case this message is
 * nearly always about. It is not universal — a poster-sized PNG of flat colour
 * that is already 1,200px wide will barely move — and a promise that fails for
 * somebody is how the rest of the sentence stops being believed.
 */
export function templateImageTooLargeMessage(filename: string, byteSize: number): string {
  const { size, cap } = imageSizeFigures(byteSize, MAX_TEMPLATE_ASSET_BYTES);
  const name = filename.trim() === '' ? 'That image' : `“${filename.trim()}”`;
  return (
    `${name} is ${size}, and an image in an email has to be ${cap} or smaller, so it was not added to this template. ` +
    `Export it again at about ${groupDigits(EMAIL_IMAGE_MAX_WIDTH_PX)} pixels wide — twice the width it is shown at — which for a photograph is usually a fraction of the size. ` +
    `Wend does not resize pictures for you.`
  );
}

/**
 * The same refusal in a few words, for the field error beside the control.
 *
 * A field error sits under a form control and is read at a glance, so it carries
 * the limit and nothing else. The full sentence above is what the alert says.
 */
export const TEMPLATE_IMAGE_TOO_LARGE_FIELD_MESSAGE = `Images must be ${formatTemplateAssetBytes(MAX_TEMPLATE_ASSET_BYTES)} or smaller.`;

/**
 * What to say when the **request itself** was too big to read — the multipart
 * ceiling, not either per-kind cap.
 *
 * One route carries both kinds of file, so multer's own limit has to be the
 * larger of the two ({@link MAX_ATTACHMENT_BYTES}) and the per-kind cap is
 * asserted afterwards. That means a `LIMIT_FILE_SIZE` **cannot** know whether
 * the file was an image or an attachment, and cannot know how big it actually
 * was either: multer stops reading at the limit, so the size is not a number
 * anybody measured. Printing one would be inventing it.
 *
 * So this sentence names neither. It quotes the limit that was really applied
 * and then both per-kind caps, because the person has to be able to work out
 * which one they are up against. The wording it replaced quoted the
 * inline-image cap for every file on this route — telling somebody their 12 MB
 * PDF was "larger than the 1 MB limit for an inline image", which is wrong about
 * the limit, wrong about the kind, and sends them to compress the wrong thing.
 */
export const TEMPLATE_UPLOAD_OVER_REQUEST_LIMIT_MESSAGE =
  `That file is too large to upload, so nothing was stored. ` +
  `An attachment has to be ${formatTemplateAssetBytes(MAX_ATTACHMENT_BYTES)} or smaller, ` +
  `and a picture in the body of the email ${formatTemplateAssetBytes(MAX_TEMPLATE_ASSET_BYTES)} or smaller.`;

/** The same, in a few words, for the field error beside the control. */
export const TEMPLATE_UPLOAD_OVER_REQUEST_LIMIT_FIELD_MESSAGE = `This file is over the ${formatTemplateAssetBytes(MAX_ATTACHMENT_BYTES)} upload limit.`;

/**
 * What to say about a picture that is close to the cap. **Null when it is not**,
 * so there is no boolean to get the wrong way round and no caller that can print
 * the warning about a 40 KB logo.
 *
 * It is not a refusal and does not pretend to be: the image is already uploaded
 * and will send. What it claims is only what is true — the size, and that the
 * recipient pays for it. This list's readers are frequently on mobile data in a
 * district town, which is the reason the number matters at all and the reason
 * the sentence talks about the reader rather than about storage.
 *
 * **"AT" AND "CLOSE TO" ARE DIFFERENT STATEMENTS AND ONLY ONE IS EVER TRUE.**
 * The warning band runs from {@link TEMPLATE_IMAGE_NEAR_LIMIT_BYTES} up to and
 * **including** {@link MAX_TEMPLATE_ASSET_BYTES} — the cap refuses what is
 * *above* it, so a file of exactly the cap is accepted and lands here. Saying a
 * file is "close to" a limit it is exactly equal to is the same shape of
 * falsehood as rendering an unmeasured figure as a zero: the sentence is
 * grammatical, it is confident, and it is not what happened. So the standing is
 * decided by `===` against the cap and the two cases say different things.
 */
export function templateImageNearLimitMessage(byteSize: number): string | null {
  if (byteSize < TEMPLATE_IMAGE_NEAR_LIMIT_BYTES) return null;
  const { size, cap } = imageSizeFigures(byteSize, MAX_TEMPLATE_ASSET_BYTES);
  const exportAdvice = `exporting it at about ${groupDigits(EMAIL_IMAGE_MAX_WIDTH_PX)} pixels wide is worth the minute it takes`;

  /*
   * ABOVE THE CAP IS A THIRD STANDING, AND IT IS REACHABLE.
   *
   * The cap refuses an *upload* and nothing re-checks a stored file (see
   * {@link MAX_TEMPLATE_ASSET_BYTES}), so a picture uploaded while the cap was
   * 2 MB can be 1.6 MB on a template today and keeps sending. This function used
   * to call that "close to the 1 MB limit" — false twice over: it is over the
   * limit, and uploading the same file again is refused. So it says both.
   *
   * `>` against the cap, not a comparison of the rendered figures: a file just
   * over the cap can render as "1 MB", and `imageSizeFigures` then drops both
   * figures to exact bytes, which is what keeps "over" readable.
   */
  if (byteSize > MAX_TEMPLATE_ASSET_BYTES) {
    return (
      `This image is ${size}, over the ${cap} limit for a picture uploaded today — it was added before the limit was lowered, and it still sends. ` +
      `Uploading this same file again would be refused, and every recipient whose mail app shows it downloads all of it, often on mobile data, so ${exportAdvice}.`
    );
  }

  const standing =
    byteSize === MAX_TEMPLATE_ASSET_BYTES ? `exactly the ${cap} limit` : `close to the ${cap} limit`;
  /*
   * "Every recipient whose mail app shows it", not "every recipient": where
   * pictures are hosted, a client that blocks remote content downloads nothing,
   * and this sentence is shown in both delivery modes.
   */
  return (
    `This image is ${size}, ${standing}. It will send, but every recipient whose mail app shows it downloads it — ` +
    `often on mobile data — so ${exportAdvice}.`
  );
}

/**
 * What a file **attachment** may be. A closed allow-list, mirrored as the
 * `template_assets_mime_type_is_supported` CHECK in
 * `0009_attachment_mime_types.sql`.
 *
 * An allow-list rather than a deny-list, and the difference matters more here
 * than anywhere else in the codebase: a deny-list needs a new rule for every
 * executable format that has ever existed and every one that will. This needs
 * none. Anything not named below is refused, including the next thing nobody
 * has thought of.
 *
 * SVG is absent from **both** lists on purpose. It is the one "image" that is
 * really a document — an XML script container — and it stays excluded even
 * though a recipient would only ever download it.
 *
 * Archives (`.zip`, `.7z`, `.rar`) are absent too. An archive is a container for
 * exactly the things this list refuses, so admitting one would undo the list.
 * Add them only alongside a decision about what may be *inside* one.
 */
export const TEMPLATE_ATTACHMENT_MIME_TYPES = [
  // Images, so a picture can be sent as a file rather than embedded in the body.
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  // Documents.
  'application/pdf',
  'text/csv',
  'text/plain',
  // Office, legacy — OLE2 compound files (.xls, .ppt, .doc).
  'application/vnd.ms-excel',
  'application/vnd.ms-powerpoint',
  'application/msword',
  // Office, OOXML — zip containers (.xlsx, .pptx, .docx).
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
] as const;
export const templateAttachmentMimeTypeSchema = z.enum(TEMPLATE_ATTACHMENT_MIME_TYPES);
export type TemplateAttachmentMimeType = z.infer<typeof templateAttachmentMimeTypeSchema>;

export function isTemplateAttachmentMimeType(value: string): value is TemplateAttachmentMimeType {
  return (TEMPLATE_ATTACHMENT_MIME_TYPES as readonly string[]).includes(value);
}

/**
 * The file extension each attachment type is expected to carry.
 *
 * This exists because **the bytes cannot always identify the format**, and
 * pretending otherwise would be the dishonest option:
 *
 *   - a PDF is unambiguous — it starts `%PDF-`;
 *   - `.xlsx`, `.pptx` and `.docx` are all **zip containers**. Their bytes start
 *     `PK\x03\x04` and are indistinguishable without unzipping and reading the
 *     central directory;
 *   - `.xls`, `.ppt` and `.doc` are all **OLE2 compound files**, likewise
 *     identical for the first eight bytes;
 *   - a CSV has no signature at all. No sequence of bytes makes a file a CSV.
 *
 * So the upload does two separate things and says which is which: it **verifies
 * the container** from the bytes — this really is a zip, an OLE2 file, a PDF, or
 * decodable text — and it **takes the variant** from the extension. The security
 * property is carried entirely by the first: an executable cannot masquerade as
 * a spreadsheet, because it is not a zip. The second only decides the
 * `Content-Type` a recipient's mail client sees, and getting that wrong shows
 * the wrong icon rather than running anything.
 */
export const TEMPLATE_ATTACHMENT_EXTENSIONS: Readonly<
  Record<TemplateAttachmentMimeType, readonly string[]>
> = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/gif': ['.gif'],
  'image/webp': ['.webp'],
  'application/pdf': ['.pdf'],
  'text/csv': ['.csv'],
  'text/plain': ['.txt'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.ms-powerpoint': ['.ppt'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
};

/** Every accepted extension, for the file picker's `accept` attribute. */
export const TEMPLATE_ATTACHMENT_ACCEPT = Object.values(TEMPLATE_ATTACHMENT_EXTENSIONS)
  .flat()
  .join(',');

/** The attachment type an extension implies, or null. Case-insensitive. */
export function attachmentMimeTypeForExtension(filename: string): TemplateAttachmentMimeType | null {
  const lower = filename.toLowerCase();
  for (const [mimeType, extensions] of Object.entries(TEMPLATE_ATTACHMENT_EXTENSIONS)) {
    if (extensions.some((extension) => lower.endsWith(extension))) {
      return mimeType as TemplateAttachmentMimeType;
    }
  }
  return null;
}

/* -------------------------------------------------------------------------- */
/* The public address of a hosted image                                        */
/* -------------------------------------------------------------------------- */

/**
 * How many random bytes a public asset id is made of. **16 = 128 bits**, from a
 * CSPRNG, hex-encoded.
 *
 * The single source for both halves: the minting side reads this to generate,
 * and {@link templateAssetPublicIdSchema}'s pattern is built from it, so the
 * length in the regex cannot drift away from the length that is generated.
 *
 * 128 bits because **the id is the access control**. The endpoint that resolves
 * one is unauthenticated by necessity — a mail client sends no Authorization
 * header — so an id short enough to enumerate is a bucket anyone can walk.
 */
export const TEMPLATE_ASSET_PUBLIC_ID_BYTES = 16;

const TEMPLATE_ASSET_PUBLIC_ID_PATTERN = new RegExp(
  `^[0-9a-f]{${TEMPLATE_ASSET_PUBLIC_ID_BYTES * 2}}$`,
);

/**
 * The opaque id an inline image is hosted under — `template_assets.public_id`.
 *
 * **It is deliberately NOT the asset id, and not derived from it.**
 *
 * The obvious shortcut is to reuse `assetId`: it is already a uuid, already
 * unique, already on the row. The reason not to is that an asset id is not a
 * private value — it is in the storage key, it was the `cid:` a message used to
 * carry, it is in the authenticated API path the editor loads, and it is in the
 * template document itself. Reusing it would make the one identifier a stranger
 * can read off an `<img src>` into the join key for every internal one. A second
 * value of identical entropy and no relation to the first costs one column and
 * removes that join entirely. The database refuses the shortcut outright — see
 * `template_assets_public_id_is_not_the_asset_id` in `0021`, which compares
 * `public_id` against the dash-stripped `id` precisely because "strip the
 * dashes off the uuid" is the way this gets implemented wrong.
 *
 * **It is also not the storage key**, which is the other tempting shortcut —
 * the key is already unique per object. The key is
 * `tenants/<tenantId>/templates/<templateId>/assets/<assetId>.jpg`, and a tenant
 * id in an `<img src>` is a **stable correlation identifier visible in
 * view-source**. Two emails collected from two different people carrying the
 * same tenant id join into "these two people bank with the same cooperative
 * bank" — a sensitive inference, in a market covered by the DPDP Act, handed to
 * anybody who can read a message source. So the private key stays byte-for-byte
 * as it was, which also leaves `template_assets_storage_key_shape` and
 * `unique(tenant_id, storage_key)` untouched by this whole design.
 *
 * Shape: lower-case hex, because there is then no case for a mail client or a
 * link rewriter to mangle and no `+` or `/` to be percent-encoded on the way.
 * No prefix — a prefix is vocabulary inside a string read by strangers and buys
 * them nothing. No dashes, so a value that is visibly a uuid was not minted
 * here.
 */
export const templateAssetPublicIdSchema = z
  .string()
  .regex(
    TEMPLATE_ASSET_PUBLIC_ID_PATTERN,
    `A public asset id is ${TEMPLATE_ASSET_PUBLIC_ID_BYTES * 2} lower-case hexadecimal characters, generated by the server`,
  );
export type TemplateAssetPublicId = z.infer<typeof templateAssetPublicIdSchema>;

/**
 * The path a hosted image is served at, under the configured base.
 *
 * **The base is configuration; the path is contract.** That split is the point
 * of this section. The host differs per deployment and `@wend/shared` cannot
 * know it; the shape underneath it is a promise the send path and whatever
 * serves the bytes both have to keep, so it lives here where neither side can
 * quietly redefine it.
 *
 * One segment and nothing else: no tenant, no template, no asset id, no file
 * extension. Every one of those is a fact about the sender or about the document
 * leaking into a string that strangers read, and the public id alone resolves
 * without any of them. (The extension in particular is not needed and not
 * wanted — the stored `Content-Type` is what tells a client what it received.)
 */
export const TEMPLATE_ASSET_PUBLIC_PATH_PREFIX = '/i';

/** The path component for a public id. Pure, total, and takes no configuration. */
export function templateAssetPublicPath(publicId: string): string {
  return `${TEMPLATE_ASSET_PUBLIC_PATH_PREFIX}/${publicId}`;
}

/**
 * The environment variable that carries the image host, **as a value rather than
 * as prose**.
 *
 * Saying in a comment that a name is fixed and then typing it out again in each
 * place that quotes it is the drift a constant prevents. The places that quote
 * it are the operator's: the API's environment contract and its boot refusals,
 * the send-path refusal in `message-source.ts` for a host that fails its own
 * shape check, and the boot log line that says which {@link TemplateImageDelivery}
 * a process is running. A rename that missed one would send a reader hunting for
 * a variable that does not exist.
 *
 * **The customer-facing refusal used to quote it and no longer does**, and the
 * reason is the gate. `template_images_not_hostable` only fires where this
 * variable is already set — unset, images are embedded and there is nothing to
 * refuse — so telling a customer to go and set it would be telling them to set
 * something that is set. What they can actually do is in
 * {@link templateImagesUnpublishedMessage}.
 */
export const TEMPLATE_ASSET_PUBLIC_BASE_URL_ENV = 'TEMPLATE_ASSET_PUBLIC_BASE_URL';

/**
 * The configured origin hosted images are served from — `https://img.example.in`,
 * or `https://cdn.example.com/wend` when a path prefix is wanted.
 *
 * The deployment spells it {@link TEMPLATE_ASSET_PUBLIC_BASE_URL_ENV}. **Whether
 * it is set is the whole of {@link TemplateImageDelivery}**: set means every
 * image is hosted, unset means every image is embedded. That makes a value this
 * schema refuses more dangerous than an ordinary bad setting — a deployment that
 * meant to host must not quietly fall back to embedding, and must never start
 * hosting to an address that serves nothing.
 *
 * Refused **here**, at the shape, so a misconfiguration is a process that will
 * not boot rather than a thousand messages carrying broken pictures. What it
 * refuses and why:
 *
 *   - **a trailing slash** — so there is exactly one spelling of a given base
 *     and {@link templateAssetPublicUrl} can be plain concatenation rather than
 *     a join with an edge case;
 *   - **a query or a fragment** — neither survives having a path appended, and a
 *     base carrying one is a base somebody has misunderstood;
 *   - **userinfo** (`https://user:pass@host`) — credentials in a URL that goes
 *     out to every recipient;
 *   - **anything but `https:`** — this URL is fetched by strangers on networks
 *     we know nothing about, and an image loaded over plain HTTP is
 *     interceptable and is flagged by some clients;
 *   - **a host that is not a host** — an empty one (`https://:443`), one that is
 *     not a DNS name (`https://%%%`, `https://a..b`), or a port that is not a
 *     port (`:99999`). Each of these used to pass: the checks above only
 *     looked *around* the host, so a malformed value booted a deployment into
 *     hosted mode with an address nothing can resolve;
 *   - **loopback, by name** — `localhost`, `*.localhost`, `127.x.x.x`, `0.0.0.0`,
 *     `[::1]`. See the next paragraph: the argument against loopback was never
 *     about the scheme, and `https://localhost` passed the scheme check.
 *
 * What it still does NOT refuse, deliberately: a private-network address or a
 * `.local` name. Both are reachable by *somebody* in some deployment, and the
 * verify scripts' fabricated `https://images.verify.wend.local` is one; the
 * rule here is "provably no recipient", not "probably".
 *
 * **THERE USED TO BE A LOOPBACK EXCEPTION, AND IT IS GONE.** `http://localhost`
 * and `http://127.0.0.1` were allowed "because a developer running a local object
 * store is serving nobody". That reasoning is exactly backwards once the variable
 * decides the delivery mode: an address only this machine can reach is an
 * address **no recipient** can reach, so accepting it is accepting a deployment
 * that hosts every picture at a URL that serves nothing. No stub needs it either
 * — `s3_stub` serves nothing whatever the address says, and the verify scripts
 * all use an `https:` host that is never fetched. No exception: https, and not
 * loopback under any scheme — the first version of this paragraph checked only
 * the scheme, which left `https://localhost` booting into hosted mode.
 *
 * The parsing itself moved to `./public-base-url.js` in slice 19, when
 * `API_BASE_URL` needed the same refusals in its own words; this setting passes
 * the words above's refusals have always used, and the list of what is refused,
 * and why, stays here where it was argued.
 */
export const templateAssetPublicBaseUrlSchema = publicHttpsBaseUrlSchema({
  subject: 'The image host',
  example: 'https://img.example.in',
  whyHttps: "every recipient's mail client fetches pictures from it, over networks nobody here controls",
  whyNoCredentials: 'this URL is sent to every recipient',
  whyNotLoopback: "no recipient's mail client can reach it, so every picture would arrive broken",
});

/**
 * The absolute URL for one hosted image. **Null when it cannot be built.**
 *
 * Null rather than a throw, and null rather than a best-effort string, because
 * of where this is called: the send path, per campaign, immediately before
 * bytes go to a provider. A function that returned `"undefined/i/undefined"` on
 * bad input would put that string in a thousand inboxes; a function that threw
 * would surface as a stack trace on a campaign screen. Returning null forces
 * the caller to have an answer for "there is no URL for this image", which is
 * the fail-closed behaviour the schedule-time blocker
 * (`template_images_not_hostable` in `campaignScheduleIssues`) is supposed to
 * have caught already — this is the second lock on the same door.
 *
 * It is **pure and takes the base as an argument**, never reading configuration
 * itself, so it stays usable from the API, a verify script and (if it is ever
 * wanted) a front end, and so that the one place the deployment's host enters
 * the outgoing HTML is a parameter a reader can follow.
 */
export function templateAssetPublicUrl(baseUrl: string, publicId: string): string | null {
  if (!templateAssetPublicBaseUrlSchema.safeParse(baseUrl).success) return null;
  if (!templateAssetPublicIdSchema.safeParse(publicId).success) return null;
  return `${baseUrl.trim()}${templateAssetPublicPath(publicId)}`;
}

/**
 * What to tell a person whose template has images with no public address, in a
 * deployment that hosts images. One sentence, said in two places: as the
 * campaign's `template_images_not_hostable` blocker, and in the template editor
 * beside the Save button that fixes it.
 *
 * ## The state it describes, and why it is ordinary rather than a fault
 *
 * `public_id` is minted when an image is uploaded **into a deployment that
 * hosts**. Every image uploaded before `TEMPLATE_ASSET_PUBLIC_BASE_URL` was set
 * has none — which, on the deployment that sent the first real campaign, is
 * every image there is. Switching hosting on therefore makes each of those
 * templates unschedulable at once. That is the correct refusal (sending them
 * would be a broken picture in every inbox), and it is only acceptable because
 * there is a way out that needs no database edit.
 *
 * ## The way out, and why the sentence names it rather than a variable
 *
 * **Saving the template publishes every picture its document references that
 * has no address yet** — `publishInlineImages` in the API streams the private
 * copy to the public prefix and records the id — and the editor enables Save
 * for exactly this case even when nothing was edited, so "save it" is a button a
 * person can press rather than an instruction that needs a dummy change first.
 * Publishing can still fail for one picture: its stored bytes are gone, or the
 * row never recorded a content type (a picture served without one downloads
 * rather than renders, so it is refused rather than guessed). Neither is fixable
 * by saving again, and both are fixed by removing the picture and uploading it
 * afresh — which is why that is the sentence's second instruction and not a
 * footnote.
 *
 * **"Bytes are gone" has one cause an operator can prevent, and it is the likely
 * one.** Hosting requires `STORAGE_TRANSPORT=s3`; a deployment that ran on local
 * disk (the one that sent the first campaign did) has every existing picture and
 * attachment in a directory the new bucket has never seen. Unless those objects
 * are copied into the bucket under the same keys *before* the switch, saving
 * publishes nothing, every picture has to be re-uploaded, and every attachment is
 * missing at send time too. That is an operator's step, not a customer's, so it
 * is written where the operator switches hosting on (`apps/api/.env.example`)
 * and in the API's log line when a publish finds no file — not in this sentence,
 * which a customer reads. "That picture" is findable because the editor marks the card of
 * every picture with no address ({@link TEMPLATE_IMAGE_UNPUBLISHED_BLOCK_MESSAGE});
 * a count alone could not say which one it is.
 *
 * It **does not** name `TEMPLATE_ASSET_PUBLIC_BASE_URL`, and the earlier wording
 * did. This refusal only exists where that variable is already set, so telling a
 * customer to ask for it to be configured sent them to ask for something that
 * was done.
 *
 * `unpublished` and `referenced` are both counts of distinct assets the document
 * points at. The caller guarantees `0 < unpublished <= referenced`; there is no
 * sentence for zero because zero is not a refusal.
 */
export function templateImagesUnpublishedMessage(unpublished: number, referenced: number): string {
  const subject =
    referenced === 1
      ? 'The image in this template has'
      : unpublished === referenced
        ? `All ${String(referenced)} images in this template have`
        : `${String(unpublished)} of the ${String(referenced)} images in this template ${unpublished === 1 ? 'has' : 'have'}`;
  const pronoun = unpublished === 1 ? 'it' : 'they';
  return (
    `${subject} no address on Wend's image host yet, so ${pronoun} would arrive as a broken picture in every inbox. ` +
    `That is normal for a picture uploaded before Wend started hosting pictures. ` +
    `Open the template and save it: saving publishes every picture that is missing an address. ` +
    `If this is still here after saving, remove that picture from the template and upload it again.`
  );
}

/**
 * The same fault, for files, on a deployment that links to them.
 *
 * Counted rather than named, exactly as the picture version is, and for the same
 * reason: this sentence is read on a campaign screen, where nobody is holding
 * the template's file list. The instruction is the one that fixes almost every
 * case — a file uploaded before linking was switched on has no address because
 * none was minted then, and saving the template mints one.
 *
 * It says "no way to reach it" rather than "broken link", because that is what a
 * reader would actually experience: a message with no attachment and no mention
 * of one. A picture that fails this way arrives visibly broken; a file that does
 * arrives as nothing at all.
 */
export function templateAttachmentsUnpublishedMessage(unpublished: number, total: number): string {
  /*
   * The picture version's three arms, in the same order and for the same
   * reasons. The first draft of this had two — "The file" and "Both files" —
   * which is right for one file and for two and tells a tenant holding four
   * that there are two. `MAX_TEMPLATE_ATTACHMENTS` is ten.
   */
  const subject =
    total === 1
      ? 'The file on this template has'
      : unpublished === total
        ? `All ${String(total)} files on this template have`
        : `${String(unpublished)} of the ${String(total)} files on this template ${unpublished === 1 ? 'has' : 'have'}`;
  const pronoun = unpublished === 1 ? 'it' : 'them';
  return (
    `${subject} no address on Wend's asset host yet, so the email would go out with no way to reach ${pronoun}. ` +
    `That is normal for a file uploaded before Wend started linking to files. ` +
    `Open the template and save it: saving publishes every file that is missing an address. ` +
    `If this is still here after saving, remove that file from the template and upload it again.`
  );
}

/**
 * The same fault said on **the one image card it is about**, in the editor of a
 * deployment that hosts images.
 *
 * {@link templateImagesUnpublishedMessage} counts — "1 of the 3 images" — and its
 * last instruction is "remove that picture and upload it again". A count cannot
 * say *which* picture. After a save has published everything it could, the
 * picture left over is exactly the one saving cannot fix (its private bytes are
 * gone, or its row never recorded a content type), and without a mark on its
 * card a person holding five pictures would have to re-upload all five, or guess,
 * to follow an instruction that names one. So the card whose asset has no
 * `publicId` says so itself, and the counting sentence's "that picture" has
 * something to point at.
 *
 * Said only where `imageDelivery === 'hosted'`. Where pictures are embedded a
 * null `publicId` is how every picture is, and marking every card would be a
 * warning about the normal state.
 *
 * Owned here rather than typed into `BlockEditor` for the reason every sentence
 * on that card is: one wording, and the two sentences about the same fault
 * cannot drift into telling a person two different fixes.
 */
export const TEMPLATE_IMAGE_UNPUBLISHED_BLOCK_MESSAGE =
  "This picture has no address on Wend's image host yet, so it would arrive broken. Saving the template publishes it; if this is still here after saving, remove this block and upload the picture again.";

/**
 * An uploaded template file, as the API reports it.
 *
 * **Two addresses, and they are not interchangeable.**
 *
 * `url` is an **API path** — tenant-scoped, authenticated, served by us. It is
 * what the editor and the preview load, and it is what `html_compiled` holds.
 * It is not a storage URL and never becomes one.
 *
 * `publicId` is the other half, and it is what hosting added. Where an image host
 * is configured an inline image is served to *recipients* from it, because a
 * mail client cannot send an Authorization header and the bytes no longer ride
 * inside the message — see {@link imageBlockSchema} and
 * {@link TemplateImageDelivery}. Where none is configured the bytes ride inside
 * the message as they always did and `publicId` stays null, which is not a fault
 * there. The public address is minted from
 * `publicId` at **send time** and never at compile time, which is what keeps
 * `html_compiled` a pure function of the document: the compile still takes no
 * configuration, so the same document still compiles to the same bytes in every
 * deployment and the byte-identical preview guarantee still means what it says.
 *
 * The earlier version of this header argued that "handing out a bucket URL here
 * would give that back" — give back the control that comes from us deciding
 * where the bytes come from. That is still exactly right about a *storage* URL
 * and about an author-typed `src`, and it is why neither exists. It is not an
 * argument against an opaque id that we mint, for an object we hold, resolved by
 * a host we run.
 */
export const templateAssetSchema = z
  .object({
    id: uuidSchema,
    kind: templateAssetKindSchema,
    /** The uploaded file's own name, kept for display. */
    filename: z.string().nullable(),
    mimeType: z.string().nullable(),
    byteSize: z.number().int().min(0).nullable(),
    /** API path, e.g. `/api/templates/<id>/assets/<id>`. Never a storage URL. */
    url: z.string(),
    /**
     * The public id these bytes are hosted under, or **null when they are not
     * hosted**.
     *
     * Null is "no public address has been minted for this file" — the normal
     * state for a file that was just uploaded and for every file in a
     * deployment with no asset host configured. It was also, until slice 23, the
     * normal state for *every attachment ever* — "attachments are not hosted at
     * all" — which `0024` overturned when it widened the CHECK enforcing it. It
     * is **not** the same statement as
     * "the publish failed", and it is certainly not a zero.
     *
     * **Where images are hosted**, a campaign whose template still has a null
     * one on a referenced image is refused at schedule time rather than sent
     * with a broken picture — see `template_images_not_hostable` in
     * `campaignScheduleIssues` — and saving the template is what mints the
     * missing ids (see {@link templateImagesUnpublishedMessage}). Where they are
     * embedded a null here refuses nothing, because nothing needs an address.
     */
    publicId: templateAssetPublicIdSchema.nullable(),
    createdAt: isoDateTimeSchema,
  })
  .strict();
export type TemplateAsset = z.infer<typeof templateAssetSchema>;

/**
 * The non-file part of the multipart upload. The file itself is checked by the
 * API against {@link MAX_TEMPLATE_ASSET_BYTES} and
 * {@link TEMPLATE_IMAGE_MIME_TYPES} — by sniffing the bytes, not by trusting the
 * client's `Content-Type` header, which is a field an uploader chooses.
 */
export const uploadTemplateAssetRequestSchema = z.object({}).strict();
export type UploadTemplateAssetRequest = z.infer<typeof uploadTemplateAssetRequestSchema>;

export const uploadTemplateAssetResponseSchema = z.object({ asset: templateAssetSchema });
export type UploadTemplateAssetResponse = z.infer<typeof uploadTemplateAssetResponseSchema>;

export const templateAssetParamsSchema = z.object({ templateId: uuidSchema, assetId: uuidSchema });
export type TemplateAssetParams = z.infer<typeof templateAssetParamsSchema>;

/* -------------------------------------------------------------------------- */
/* The frame — body width and background (slice 56)                             */
/* -------------------------------------------------------------------------- */

/**
 * How wide the email is.
 *
 *   - `standard` — today's email: a 600px column, every text and image block
 *     inset 24px each side, so content is at most 552px wide — the cap the
 *     owner asked to be able to lift. The default, and **it compiles byte for byte to
 *     what every template compiled to before this setting existed** —
 *     `verify-template-golden` holds the renderer to that.
 *   - `full` — the 600px cap goes: on webmail and phones the email spans the
 *     reader's window, an image with no set width runs edge to edge with no
 *     side inset, and text KEEPS its 24px inset, because a line of text
 *     touching the edge of a phone screen is unreadable. An image with a set
 *     width keeps its width and its 24px inset too — the product owner's
 *     decision: it sits beside the text, lined up with it. Outlook for Windows
 *     cannot follow; what it does instead is written where the renderer emits
 *     it.
 *
 * A column with a CHECK, 0039, rather than a key inside `blocks`: see decision
 * 4 in this file's header.
 */
export const TEMPLATE_BODY_WIDTH_VALUES = ['standard', 'full'] as const;
export const templateBodyWidthSchema = z.enum(TEMPLATE_BODY_WIDTH_VALUES);
export type TemplateBodyWidth = z.infer<typeof templateBodyWidthSchema>;
export const DEFAULT_TEMPLATE_BODY_WIDTH: TemplateBodyWidth = 'standard';

/**
 * What is behind the content.
 *
 *   - `white` — today's email, and the default: white stated explicitly at the
 *     body, the body section and the footer section, because Outlook for
 *     Windows' own default is not white.
 *   - `transparent` — no background colour at any of the three, so the reader's
 *     own client shows through; nor on the linked-attachment section the send
 *     path adds below the footer, which follows both settings. That is exactly
 *     the cost: the text colour is
 *     still the theme's dark ink, so a client whose own background is dark —
 *     a dark-mode client that does not also invert the text — shows dark text
 *     on dark. That is why it is not the default.
 */
export const TEMPLATE_BACKGROUND_VALUES = ['white', 'transparent'] as const;
export const templateBackgroundSchema = z.enum(TEMPLATE_BACKGROUND_VALUES);
export type TemplateBackground = z.infer<typeof templateBackgroundSchema>;
export const DEFAULT_TEMPLATE_BACKGROUND: TemplateBackground = 'white';

/** The two frame settings together — what the renderer is told besides the blocks. */
export interface TemplateLayout {
  readonly bodyWidth: TemplateBodyWidth;
  readonly background: TemplateBackground;
}

/** Today's email. What every template that predates slice 56 is. */
export const DEFAULT_TEMPLATE_LAYOUT: TemplateLayout = Object.freeze({
  bodyWidth: DEFAULT_TEMPLATE_BODY_WIDTH,
  background: DEFAULT_TEMPLATE_BACKGROUND,
});

/* -------------------------------------------------------------------------- */
/* Read                                                                        */
/* -------------------------------------------------------------------------- */

/**
 * A template, in full.
 *
 * `mjmlSource` is **not** on this shape. It is build output, it is large, and
 * putting it on the read shape would invite an editor that round-trips it — the
 * exact thing decision 1 exists to prevent. `htmlCompiled` is present because
 * the preview needs something to render and slice 5 needs something to send;
 * it is read-only, and no write shape in this file accepts it.
 */
export const templateSchema = z
  .object({
    id: uuidSchema,
    name: z.string(),
    type: templateTypeSchema,
    status: templateStatusSchema,
    subject: z.string().nullable(),
    preheader: z.string().nullable(),
    replyTo: z.string().nullable(),
    /**
     * The email document. The source of truth for `type: 'email'`; everything
     * else on an email template is derived from it.
     *
     * **Null on a WhatsApp template**, and that is the shape 0012 enforces from
     * the database side (`templates_whatsapp_has_no_email_columns` lists
     * `blocks`): a WhatsApp template carrying a document would pick up an
     * unsubscribe footer it must not have, because WhatsApp opt-out is a reply
     * and a Meta setting, not a link we compile.
     */
    blocks: templateDocumentSchema.nullable(),
    /**
     * The WhatsApp half. Null on an email template — the two are disjoint, and
     * the database refuses a row that is half of each.
     */
    whatsapp: z.lazy(() => waTemplateViewSchema).nullable(),
    /** Build output. Null until the template has been compiled. Never authored. */
    htmlCompiled: z.string().nullable(),
    compiledAt: isoDateTimeSchema.nullable(),
    /** Derived from the document on every save. Never typed by a person. */
    variables: z.array(z.string()),
    /** Inline images uploaded against this template. */
    assets: z.array(templateAssetSchema),
    /**
     * How this deployment will carry this template's pictures when it sends —
     * {@link TemplateImageDelivery}. **Null on a WhatsApp template**, which has
     * no blocks and so no inline pictures, for the same reason `blocks` is.
     *
     * A deployment fact carried on the template, deliberately, rather than a
     * second request the editor has to make and could forget. The editor needs
     * it for two sentences that are true in one mode and false in the other —
     * what alt text is for, and whether a picture counts towards a message's
     * size — and for one control: where images are hosted, Save is offered
     * whenever a picture on the document has no `publicId`, because saving is
     * what publishes it. Computed by the API from the same derived value the send
     * path uses, so the screen cannot describe a mode the send path is not in.
     */
    imageDelivery: templateImageDeliverySchema.nullable(),
    /**
     * Slice 56: the frame — {@link TemplateBodyWidth}, {@link TemplateBackground}.
     * Always present: the columns are `NOT NULL` with today's values as their
     * defaults, so a template that predates them reads as `standard` / `white`.
     * On a WhatsApp template they are those defaults and mean nothing.
     */
    bodyWidth: templateBodyWidthSchema,
    background: templateBackgroundSchema,
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
  })
  .strict();
export type Template = z.infer<typeof templateSchema>;

export const templateResponseSchema = z.object({ template: templateSchema });
export type TemplateResponse = z.infer<typeof templateResponseSchema>;

/**
 * Row shape for the templates list.
 *
 * No `blocks` and no HTML: a list of twenty templates would otherwise carry
 * twenty documents and twenty compiled emails to draw twenty rows. `blockCount`
 * is what the row actually shows.
 */
export const templateSummarySchema = z
  .object({
    id: uuidSchema,
    name: z.string(),
    type: templateTypeSchema,
    status: templateStatusSchema,
    subject: z.string().nullable(),
    /** Blocks in the email document. **0 on a WhatsApp template**, which has none. */
    blockCount: z.number().int().min(0),
    /** Meta's word for a WhatsApp template, verbatim. Null for email. */
    metaStatus: z.string().nullable().default(null),
    /** Null when the template has never compiled — i.e. it has no preview yet. */
    compiledAt: isoDateTimeSchema.nullable(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
    /**
     * How many campaigns have actually **sent** using this template.
     *
     * `sent_count > 0`, not merely a campaign that references it: a draft that
     * names a template has not used it, and counting one would tell a reader
     * their template is proven when nothing has gone out. It is the same
     * predicate `unused_templates` uses in `overview-opportunities.ts`, and the
     * two must agree — a template this says was used 0 times is exactly a
     * template that opportunity counts as never used.
     */
    useCount: z.number().int().min(0),
  })
  .strict();
export type TemplateSummary = z.infer<typeof templateSummarySchema>;

export const TEMPLATE_SORT_VALUES = ['createdAt', 'updatedAt', 'name'] as const;
export const templateSortSchema = z.enum(TEMPLATE_SORT_VALUES);
export type TemplateSort = z.infer<typeof templateSortSchema>;

export const templateListQuerySchema = paginationQuerySchema.extend({
  /** Matches the template name, case-insensitive. */
  search: z.string().trim().max(120).optional(),
  type: templateTypeSchema.optional(),
  /** The campaign wizard in slice 5 lists `approved` only. */
  status: templateStatusSchema.optional(),
  sort: templateSortSchema.default('updatedAt'),
  direction: sortDirectionSchema.default('desc'),
});
export type TemplateListQuery = z.infer<typeof templateListQuerySchema>;

export const templateListResponseSchema = paginated(templateSummarySchema);
export type TemplateListResponse = z.infer<typeof templateListResponseSchema>;

export const templateParamsSchema = z.object({ templateId: uuidSchema });
export type TemplateParams = z.infer<typeof templateParamsSchema>;

/* -------------------------------------------------------------------------- */
/* Write                                                                       */
/* -------------------------------------------------------------------------- */

export const templateNameSchema = z.string().trim().min(1).max(MAX_TEMPLATE_NAME_LENGTH);

/** Optional line. A cleared input arrives as `''` and is stored as NULL. */
function optionalLine(max: number) {
  return z
    .string()
    .trim()
    .max(max)
    .refine((value) => !CONTROL_CHARACTERS.test(value) && !/[\r\n]/.test(value), 'This field is a single line')
    // Subject and preheader both reach `html_compiled`, so they inherit the
    // jsonb-versus-text mismatch described on LONE_SURROGATE.
    .refine((value) => !LONE_SURROGATE.test(value), UNPRINTABLE_MESSAGE)
    .transform((value) => (value === '' ? null : value))
    .nullable();
}

export const templateSubjectSchema = optionalLine(MAX_SUBJECT_LENGTH);
export const templatePreheaderSchema = optionalLine(MAX_PREHEADER_LENGTH);

/**
 * Reply-To. Normalised the same way every other address in Wend is, so the
 * `templates_reply_to_is_lowercase` CHECK cannot be the thing that discovers a
 * capitalised paste.
 */
export const templateReplyToSchema = z
  .string()
  .trim()
  .toLowerCase()
  .max(254)
  .email()
  .nullable()
  .or(z.literal('').transform(() => null));

/** Runs the save-time variable refusal as part of parsing the request. */
function refuseUnresolvableVariables(
  body: { blocks?: readonly TemplateBlock[]; subject?: string | null; preheader?: string | null },
  ctx: z.RefinementCtx,
): void {
  if (!body.blocks) return;
  for (const issue of unresolvableTemplateVariables({
    blocks: body.blocks,
    subject: body.subject ?? null,
    preheader: body.preheader ?? null,
  })) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: issue.location.kind === 'block' ? ['blocks'] : [issue.location.kind],
      message: issue.message,
    });
  }
}

/**
 * Create an **email** template — the arm slice 4 wrote, unchanged.
 *
 * `blocks` is optional. Omitted, the API seeds {@link createDefaultDocument} —
 * which is how "the unsubscribe footer is present in every new template" holds
 * even for a template created by a caller that has never heard of a footer.
 *
 * There is no `html`, no `mjml` and no `status` field, and the object is
 * `.strict()`: HTML is derived and approval is a separate, deliberate action.
 * `.strict()` is also what refuses a `whatsapp` key here, so a body that mixes
 * the two channels is a parse error rather than a half-written row.
 */
export const createEmailTemplateRequestSchema = z
  .object({
    name: templateNameSchema,
    type: z.literal('email'),
    subject: templateSubjectSchema.optional().default(null),
    preheader: templatePreheaderSchema.optional().default(null),
    replyTo: templateReplyToSchema.optional().default(null),
    blocks: templateDocumentSchema.optional(),
    /**
     * Slice 56. Omitted is today's email — `DEFAULT_TEMPLATE_LAYOUT`, applied
     * by the API and by the column defaults. Left optional in the parsed type
     * rather than defaulted here so that every caller written before the
     * setting existed still type-checks as the request it always was.
     */
    bodyWidth: templateBodyWidthSchema.optional(),
    background: templateBackgroundSchema.optional(),
  })
  .strict();

/**
 * Create a **WhatsApp** template.
 *
 * No subject, no preheader, no reply-to and no blocks — none of them exist on
 * this channel, and `.strict()` says so rather than accepting and ignoring
 * them. Status is not here either: a WhatsApp template starts as a `draft` and
 * only Meta moves it, through `POST /templates/:id/submit` and the approval
 * webhook.
 */
export const createWhatsappTemplateRequestSchema = z
  .object({
    name: templateNameSchema,
    type: z.literal('whatsapp'),
    whatsapp: z.lazy(() => waTemplateDocumentSchema),
  })
  .strict();

/**
 * Create a template — email or WhatsApp, one endpoint.
 *
 * A `z.preprocess` supplies `type: 'email'` when the caller omitted it, because
 * `z.discriminatedUnion` routes on the **raw input** and a `.default()` on the
 * discriminant would never be reached. Slice 4 callers that send no `type` keep
 * working; the union is what gives a WhatsApp body errors about WhatsApp fields
 * instead of a flat list of every way it failed to be an email.
 *
 * The unresolvable-variable refusal runs on the email arm only. WhatsApp
 * variables are positional and are checked by
 * {@link collectWaTemplateVariableIssues} — running the email parser over
 * `{{1}}` is exactly the confusion `whatsapp.ts` exists to prevent.
 */
export const createTemplateRequestSchema = z
  .preprocess(
    (raw) =>
      typeof raw === 'object' && raw !== null && !Array.isArray(raw) && !('type' in raw)
        ? { ...raw, type: 'email' }
        : raw,
    z.discriminatedUnion('type', [
      createEmailTemplateRequestSchema,
      createWhatsappTemplateRequestSchema,
    ]),
  )
  .superRefine((body, ctx) => {
    if (body.type === 'email') refuseUnresolvableVariables(body, ctx);
  });
export type CreateTemplateRequest = z.infer<typeof createTemplateRequestSchema>;

/**
 * Update a template — any subset, one endpoint.
 *
 * `blocks` is a **replacement, not a patch**: the editor holds the whole
 * document and sends the whole document, which is what makes undo, reorder and
 * delete one code path instead of a diff protocol. The footer rule is enforced
 * on the replacement, so "delete the footer and save" is a 422 rather than a
 * template that quietly loses its unsubscribe link — done-when #5, in the
 * contract rather than in a controller.
 */
export const updateEmailTemplateRequestSchema = z
  .object({
    name: templateNameSchema.optional(),
    subject: templateSubjectSchema.optional(),
    preheader: templatePreheaderSchema.optional(),
    replyTo: templateReplyToSchema.optional(),
    blocks: templateDocumentSchema.optional(),
    /** Slice 56. Omitted keeps what is stored; either one recompiles. */
    bodyWidth: templateBodyWidthSchema.optional(),
    background: templateBackgroundSchema.optional(),
  })
  .strict()
  .refine(
    (body) =>
      body.name !== undefined ||
      body.subject !== undefined ||
      body.preheader !== undefined ||
      body.replyTo !== undefined ||
      body.blocks !== undefined ||
      body.bodyWidth !== undefined ||
      body.background !== undefined,
    'Nothing to update: send a name, subject, preheader, replyTo, blocks, bodyWidth or background',
  )
  .superRefine(refuseUnresolvableVariables);

/**
 * Update a WhatsApp template — `whatsapp` is the whole document, replaced.
 *
 * Same posture as `blocks`: the composer holds the document and sends the
 * document. There is no per-field patch, and there is no `status` — a
 * resubmission to Meta is `POST /templates/:id/resubmit`, which requires a
 * person to restate what Meta refused.
 */
export const updateWhatsappTemplateRequestSchema = z
  .object({
    name: templateNameSchema.optional(),
    whatsapp: z.lazy(() => waTemplateDocumentSchema),
  })
  .strict();

/**
 * Update a template — either channel, one endpoint, no `type` field.
 *
 * There is nothing to discriminate on, so this is a plain union and the arms
 * are made mutually exclusive by `.strict()` in both directions: the WhatsApp
 * arm requires `whatsapp`, and the email arm refuses the key. A body carrying
 * `blocks` and `whatsapp` together matches neither, which is the right answer —
 * the row it would write is one the database refuses anyway.
 */
export const updateTemplateRequestSchema = z.union([
  updateWhatsappTemplateRequestSchema,
  updateEmailTemplateRequestSchema,
]);
export type UpdateTemplateRequest = z.infer<typeof updateTemplateRequestSchema>;

/**
 * Approve a draft, or send an approved template back to draft.
 *
 * `pending` and `rejected` are absent: they exist in the fixed enum for
 * WhatsApp, where Meta reviews. For email the tenant does the approving, so the
 * only two states this endpoint can reach are the two a person can choose.
 */
export const setTemplateStatusRequestSchema = z
  .object({ status: z.enum(['draft', 'approved']) })
  .strict();
export type SetTemplateStatusRequest = z.infer<typeof setTemplateStatusRequestSchema>;

/* -------------------------------------------------------------------------- */
/* Preview                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Preview a template.
 *
 * The preview endpoint runs **the same compile function** that produces
 * `html_compiled` — SLICES is explicit that there is exactly one renderer,
 * because a preview produced by a different code path is a preview that
 * eventually lies, at the worst possible moment.
 *
 * `substituteSampleValues` is the one thing that may differ, and it is a
 * post-compile step rather than a compile option:
 *
 *   blocks → MJML → HTML   (variables left as literal `{{ name }}` tokens)
 *                          ↳ this output is what `html_compiled` stores
 *   HTML   → HTML          (tokens replaced, escaped, per recipient or by sample)
 *
 * Keeping substitution outside the compile is what makes done-when #2 checkable
 * at all: with `substituteSampleValues: false` the preview HTML is
 * **byte-identical** to the stored `html_compiled`, so the two can be compared
 * rather than eyeballed. It is also why the compile must contain no timestamp,
 * no generated id and no randomness — the same document must compile to the same
 * bytes every time.
 *
 * `sampleValues` overrides individual samples so a person can see their own
 * name in the preview. Values are plain strings and are HTML-escaped by the
 * substituter, exactly like a real contact value in slice 5.
 */
export const templatePreviewRequestSchema = z
  .object({
    /**
     * Preview an unsaved document. Omitted, the stored one is compiled — which
     * is the form done-when #2 compares against `html_compiled`.
     */
    blocks: templateDocumentSchema.optional(),
    subject: templateSubjectSchema.optional(),
    preheader: templatePreheaderSchema.optional(),
    /**
     * Slice 56: preview a frame before it is saved. Each one omitted is the
     * stored value, exactly like `blocks` — so the no-argument preview is still
     * byte-identical to `html_compiled`.
     */
    bodyWidth: templateBodyWidthSchema.optional(),
    background: templateBackgroundSchema.optional(),
    substituteSampleValues: z.boolean().default(true),
    sampleValues: z.record(z.string(), z.string().max(500)).optional(),
  })
  .strict()
  .superRefine(refuseUnresolvableVariables);
export type TemplatePreviewRequest = z.infer<typeof templatePreviewRequestSchema>;

/**
 * The preview.
 *
 * The frontend renders `html` in a **sandboxed frame that cannot run script or
 * reach the network** (done-when #3) — `sandbox` with neither `allow-scripts`
 * nor `allow-same-origin`, plus a CSP that blocks remote loads. That is belt and
 * braces over the fact that block text is plain text and images are our own
 * assets: the frame is what makes the guarantee hold even for HTML this slice
 * did not produce.
 */
export const templatePreviewResponseSchema = z
  .object({
    /** The compiled email. Byte-identical to `html_compiled` when not substituted. */
    html: z.string(),
    /** Rendered as the inbox subject line, samples substituted the same way. */
    subject: z.string().nullable(),
    preheader: z.string().nullable(),
    /** Whether sample values were substituted into `html`. */
    substituted: z.boolean(),
    /** The values used, so the screen can list them beside the notice. */
    sampleValues: z.record(z.string(), z.string()),
    /** Every variable the document uses, resolvable by definition — save refuses the rest. */
    variables: z.array(z.string()),
    /**
     * What is outstanding on this template. Read `severity`: a `blocker` refuses
     * approval, `advice` is shown and never refused.
     */
    approvalIssues: z.array(
      z
        .object({
          blocker: templateApprovalBlockerSchema,
          severity: templateIssueSeveritySchema,
          message: z.string(),
        })
        .strict(),
    ),
  })
  .strict();
export type TemplatePreviewResponse = z.infer<typeof templatePreviewResponseSchema>;

/**
 * Which kind of file this upload is, as a query parameter.
 *
 * A query parameter rather than a multipart field because it has to be readable
 * *before* the body is parsed: it decides which validation the file goes
 * through, and a route that can only find out what it is holding after it has
 * finished holding it has already made the decision by default.
 *
 * **IT DOES NOT CHOOSE MULTER'S CEILING, AND THIS COMMENT USED TO SAY IT DID.**
 * The claim was "the byte ceiling multer enforces differs by kind", and it has
 * never been true of the controller: `FileInterceptor` is configured once, with
 * `MAX_ATTACHMENT_BYTES` — the larger of the two — and the per-kind cap is
 * asserted afterwards against the buffered file, which is where the 1 MB
 * inline-image refusal happens. That split is deliberate (slice 1 calls it
 * "configuration then assertion"), and the cost of it is real and worth stating
 * rather than hiding behind a sentence that describes a stricter system than the
 * one that exists: an oversized image is buffered up to 10 MB before it is
 * refused at 1 MB. What is *not* affected is the thing that matters — nothing is
 * written, and the sentence the person reads quotes the cap that actually
 * applied.
 *
 * SLICE 9 NARROWED THIS, and the previous line is worth stating because it is
 * the trap: it read `templateAssetKindSchema.default('inline_image')` — the
 * *whole* vocabulary. That was exactly right while every kind was an email
 * kind. The moment slice 9 added `wa_header_media` to that enum, the unchanged
 * line would have made `?kind=wa_header_media` a legal value on **this** route,
 * where the MIME allow-list is `TEMPLATE_IMAGE_MIME_TYPES`, the ceiling is
 * `MAX_TEMPLATE_ASSET_BYTES`, and nothing knows what a WhatsApp media format is
 * — a WA header asset stored past none of Meta's checks, which then fails at
 * submission rather than at upload.
 *
 * So it reads the **email half** of the vocabulary, which is a list that cannot
 * quietly grow when a WhatsApp kind is added. WhatsApp header media has its own
 * route and its own query, in `./whatsapp-media.js`.
 */
export const uploadTemplateAssetQuerySchema = z
  .object({ kind: z.enum(EMAIL_TEMPLATE_ASSET_KIND_VALUES).default('inline_image') })
  .strict();
export type UploadTemplateAssetQuery = z.infer<typeof uploadTemplateAssetQuerySchema>;
